import { formatImportDate, isValidImportDate, normalizeImportDate } from "./importDates";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import * as XLSX from "xlsx";
import {
  Button,
  Input,
  AppOffcanvas,
  AppModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../components/Common/AppOverlay";
import { getData, postData, postRawData } from "../../helpers/url-request";
import {
  BASE_URL,
  DOWNLOAD_IMPORT_DEFINITION_TEMPLATE,
  GET_IMPORT_DEFINITION_LIST,
} from "../../helpers/url_helper";
import {
  getClientTypes,
  getIndustryTypes,
  getReviewTemplateList,
  getUsersList,
  getWorkTypes,
  searchClients,
} from "../../helpers/backend_helper";
import { useUnsavedChanges } from "../../helpers/unsaved-changes";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import NewClientDrawer from "../../components/Clients/NewClientDrawer";
import ActionMenu from "../../components/Common/ActionMenu";
import { useContextualHelp } from "../../components/Help/ContextualHelp";
import { startGuidedTour } from "../../components/Help/GuidedTour";
import ImportDefinitionViewPage from "../Template/ImportDefinitionViewPage";
import ImportedEngagementCorrectionDrawer from "./ImportedEngagementCorrectionDrawer";
import { readExcelWorkbook, excelStructureSamples, normalizeExcelStructure } from "./excelStructure";
import EngagementUploadStep from "./EngagementUploadStep";
import EngagementMappingReview from "./EngagementMappingReview";
import {
  engagementFields,
  suggestExactMappings,
  suggestHeaderRow,
  validateHeaderRow,
  mappedSourceRows,
  canonicalEngagementRows,
} from "./engagementMapping";
import "./DataImportWizard.scss";

const importTypeDetails = {
  engagement: {
    label: "Engagement",
    icon: "bx-briefcase",
    description: "Import engagement information, metadata, and review setup.",
  },
  client: {
    label: "Client",
    icon: "bx-buildings",
    description: "Import client information and metadata into PIERS.",
  },
};

const clientBulkEditableFields = [
  {
    field: "workType",
    label: "Client Type",
    lookup: "clientTypes",
    required: true,
    allowOverwrite: true,
  },
  {
    field: "industry",
    label: "Industry",
    lookup: "industryTypes",
    allowOverwrite: true,
  },
  {
    field: "risk",
    label: "Risk Level",
    options: ["Low", "Medium", "High"],
    allowOverwrite: true,
  },
  {
    field: "isActive",
    label: "Status",
    options: ["Active", "Inactive"],
    allowOverwrite: true,
  },
];

const importTargetFor = (type) =>
  type === "client" ? "Clients" : "Engagements";

const extensionForFormat = (format) =>
  ({
    XLSX: [".xlsx"],
    CSV: [".csv"],
    XML: [".xml"],
    JSON: [".json"],
    ZIP: [".zip"],
  }[format] || []);

const formatLabel = (format) =>
  ({
    XLSX: "Excel (.xlsx)",
    CSV: "CSV (.csv)",
    XML: "XML (.xml)",
    JSON: "JSON (.json)",
    ZIP: "ZIP (.zip)",
  }[format] || format);

const templateVersionLabel = (template) =>
  template?.versionLabel || template?.version || "—";

const normaliseDefinition = (definition) => ({
  ...definition,
  id: definition.id ?? definition.Id,
  target: definition.target ?? definition.destinationEntity,
  sourceSystem: definition.sourceSystem ?? definition.sourceType ?? "PIERS",
  formatType: definition.formatType ?? "XLSX",
  version: definition.version ?? definition.versionNo ?? "1.0",
  status: definition.status ?? "Active",
  allowedExtensions: definition.allowedExtensions?.length
    ? definition.allowedExtensions
    : extensionForFormat(definition.formatType ?? "XLSX"),
  maxFileSize: definition.maxFileSize ?? 10 * 1024 * 1024,
  structure: definition.structure ?? {
    worksheet: definition.fileStructure?.worksheetName ?? definition.worksheet,
    headerRow: definition.fileStructure?.headerRow,
    dataStartRow: definition.fileStructure?.dataStartRow,
  },
  mappings: definition.mappings ?? [],
  templateStorage: definition.templateStorage ?? definition.fileStructure,
});

const firstValue = (row, names, fallback = "") => {
  const entry = Object.entries(row || {}).find(([key]) =>
    names.some(
      (name) =>
        key.replace(/[^a-z0-9]/gi, "").toLowerCase() ===
        name.replace(/[^a-z0-9]/gi, "").toLowerCase()
    )
  );
  return entry?.[1] ?? fallback;
};

const teamRolesFor = (row) => {
  const source = row?.source || {};
  return [
    [
      "Engagement Partner",
      firstValue(source, ["Engagement Partner", "Partner", "Assigned Partner"]),
    ],
    [
      "Manager",
      firstValue(source, ["Manager", "Engagement Manager", "Assigned Manager"]),
    ],
    [
      "Lead Reviewer",
      firstValue(source, ["Lead Reviewer", "Reviewer", "Assigned Reviewer"]),
    ],
    ["Assigned To", firstValue(source, ["Assigned To", "assignedTo"])],
  ].filter(
    ([, value], index, roles) =>
      value &&
      !roles
        .slice(0, index)
        .some(([, existingValue]) => existingValue === value)
  );
};

const toDateText = formatImportDate;

export const validateEngagementImportRow = (row) => {
  const validationIssues = [];
  const warningIssues = [];
  const isMissingName =
    !row.name || String(row.name).startsWith("Unnamed engagement");
  const isMissingWorkType = !row.workType || row.workType === "Not supplied";
  const requiresClientResolution = !row.clientId && !row.createClient;

  if (isMissingName)
    validationIssues.push({
      field: "Engagement Name",
      message: "Enter an engagement name in the workbook.",
    });
  if (isMissingWorkType)
    validationIssues.push({
      field: "Work Type",
      message: "Select a valid work type in the workbook.",
    });
  if (requiresClientResolution)
    validationIssues.push({
      field: "Client / Firm",
      message:
        row.clientMatchIssue ||
        "No engagement client has been selected. Choose an existing client record or create a new engagement client record.",
    });
  return {
    ...row,
    validationIssues,
    warningIssues,
    included: !row.ignored && validationIssues.length === 0,
    validation:
      validationIssues.length > 0
        ? "Error"
        : warningIssues.length > 0
        ? "Warning"
        : "Ready",
  };
};

const normalizedClientValue = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

export const resolveEngagementClient = (row, clients) => {
  if (row.clientId) return validateEngagementImportRow(row);

  const availableClients = Array.isArray(clients) ? clients : [];

  const clientName = normalizedClientValue(row.client);
  const clientCode = normalizedClientValue(row.clientCode);
  const nameMatches = clientName
    ? availableClients.filter(
        (client) => normalizedClientValue(client.name) === clientName
      )
    : [];
  const codeMatches = clientCode
    ? availableClients.filter(
        (client) => normalizedClientValue(client.code) === clientCode
      )
    : [];
  if (codeMatches.length > 1 || (!codeMatches.length && nameMatches.length > 1))
    return validateEngagementImportRow({
      ...row,
      createClient: false,
      clientMatchIssue:
        "Multiple PIERS clients match. Select the intended client.",
    });
  const nameMatch = nameMatches[0];
  const codeMatch = codeMatches[0];

  const match = codeMatch || nameMatch;
  if (match)
    return validateEngagementImportRow({
      ...row,
      clientId: match.id,
      createClient: false,
      client: match.name,
      clientCode: match.code || row.clientCode,
      clientResolution: codeMatch
        ? "Existing client matched by code"
        : "Existing client matched by name",
      clientMatchIssue: "",
    });

  return validateEngagementImportRow({
    ...row,
    createClient: true,
    clientResolution: "Engagement client record will be created",
    clientMatchIssue: "",
  });
};

const normalizeRows = (rows, importType, structure) =>
  (rows || []).map((source, index) => {
    const isClientImport = importType === "client";
    const name = firstValue(
      source,
      isClientImport ? ["Client Name", "Name"] : ["engagementName"]
    );
    const workType = firstValue(
      source,
      isClientImport ? ["Client Type", "Type"] : ["workType"]
    );
    const clientName = firstValue(
      source,
      isClientImport
        ? ["Client Code", "Client ID", "Code", "External Client ID"]
        : ["clientName"]
    );
    const clientCode = isClientImport
      ? clientName
      : firstValue(source, ["clientCode"]);
    const risk = firstValue(
      source,
      isClientImport ? ["Risk Level"] : ["risk"],
      "Medium"
    );
    const templateHint = firstValue(source, [
      "Suggested Review Template",
      "Review Template",
    ]);
    const missing =
      !name || !workType || (isClientImport && !(clientName || clientCode));
    return {
      id: `${index}-${name}`,
      sourceRow:
        Number(firstValue(source, ["__sourceRow"])) ||
        index + (structure?.dataStartRow || 2),
      source,
      name: name || `Unnamed ${isClientImport ? "client" : "engagement"}`,
      engagementCode: firstValue(source, [
        "engagementCode",
        "Engagement Code",
        "ENG_NO",
      ]),
      description: firstValue(source, ["description", "Description"]),
      externalEngagementId: firstValue(source, [
        "externalEngagementId",
        "External Engagement ID",
      ]),
      client: clientName || (isClientImport ? "Client code not supplied" : ""),
      clientCode,
      clientType: firstValue(source, ["clientType", "Client Type"]),
      workType: workType || "Not supplied",
      industry: firstValue(
        source,
        isClientImport ? ["Industry"] : ["industry"],
        "—"
      ),
      partner: firstValue(
        source,
        isClientImport
          ? ["Primary Contact Name", "Primary Contact Email", "Contact Name"]
          : ["Engagement Partner", "Partner", "Assigned Partner", "assignedTo"],
        "—"
      ),
      manager: firstValue(
        source,
        isClientImport
          ? ["Manager Email", "Manager"]
          : ["Engagement Manager", "Manager", "Assigned Manager"],
        "—"
      ),
      leadReviewer: firstValue(
        source,
        ["Lead Reviewer", "Reviewer", "Assigned Reviewer"],
        "—"
      ),
      assignedTo: firstValue(source, ["Assigned To", "assignedTo"], ""),
      primaryContactEmail: firstValue(
        source,
        isClientImport
          ? ["Primary Contact Email", "Email"]
          : ["primaryContactEmail"],
        ""
      ),
      date1904: !!structure?.date1904,
      dateOrder: structure?.dateOrder || "",
      dueDate: normalizeImportDate(firstValue(source, isClientImport ? ["Due Date"] : ["dueDate"]), { date1904: !!structure?.date1904, dateOrder: structure?.dateOrder }) || firstValue(source, isClientImport ? ["Due Date"] : ["dueDate"]),
      risk: risk || "Medium",
      isActive: !/inactive|disabled|false|0/i.test(
        String(firstValue(source, ["Status", "Is Active"], "Active"))
      ),
      overrides: {},
      suggestedTemplate: isClientImport ? "" : templateHint,
      assignedTemplate: isClientImport ? "Client defaults" : "",
      validation: missing
        ? "Error"
        : !isClientImport && !teamRolesFor({ source }).length
        ? "Warning"
        : "Ready",
      clientResolution: isClientImport
        ? missing
          ? "Required client values are missing"
          : "Ready for client creation"
        : missing
        ? "Needs resolution"
        : "Awaiting automatic client resolution",
      createClient: false,
      included: !missing,
    };
  });

function Stepper({
  currentStep,
  importType,
  onSelectStep,
  lockedType = false,
  busy = false,
  compact = false,
  maxReachableStep = currentStep,
}) {
  const steps = compact
    ? [
        [1, "Upload Engagements", "Upload your existing file"],
        [2, "Mapping", "Review and confirm column mappings"],
        [3, "Preview", "Review engagements and resolve issues"],
        [4, "Import & Results", "Track progress and view imported engagements"],
      ]
    : [
        [1, "Import Type", "Select records to import"],
        [2, "Template & Upload", "Select template and upload file"],
        [
          3,
          "Preview",
          "Check data, resolve issues, and update records.",
        ],
        [4, "Confirm & Create", "Confirm and create records"],
      ];
  return (
    <nav
      className="engagement-import__stepper"
      aria-label="Data import progress"
    >
      {steps.map(([number, title, description]) => (
        <button
          type="button"
          key={number}
          className={`engagement-import__step ${
            currentStep === number ? "is-active" : ""
          } ${currentStep > number ? "is-complete" : ""}`}
          aria-current={currentStep === number ? "step" : undefined}
          disabled={
            busy ||
            (compact
              ? number > maxReachableStep
              : currentStep <= number || (lockedType && number === 1))
          }
          onClick={() =>
            (compact ? number <= maxReachableStep : currentStep > number) &&
            onSelectStep(number)
          }
        >
          <span>
            {currentStep > number ? <i className="bx bx-check" /> : number}
          </span>
          <b>{title}</b>
          <small>{description}</small>
        </button>
      ))}
    </nav>
  );
}

function ImportHeader({
  closeAction,
  currentStep,
  importType,
  onSelectStep,
  compact = false,
  maxReachableStep,
}) {
  const { openHelp } = useContextualHelp();
  const importLabel = importTypeDetails[importType]?.label || "Data";
  return (
    <div className="modal-header canvasHeader engagement-import__modal-header">
      <div className="engagement-import__modal-header-main">
        <div className="engagement-import__modal-heading">
          <h5 className="modal-title">Data Import</h5>
          <p>
            Import {importLabel.toLowerCase()} information and metadata into
            PIERS.
          </p>
        </div>
        <Stepper
          currentStep={currentStep}
          importType={importType}
          onSelectStep={onSelectStep}
          compact={compact}
          maxReachableStep={maxReachableStep}
        />
        <div className="engagement-import__modal-header-actions">
          <button
            aria-label="Open data import help"
            className="engagement-import__help"
            onClick={openHelp}
            title="Help"
            type="button"
          >
            <i className="bx bx-help-circle" aria-hidden="true" />
          </button>
          {closeAction && (
            <button
              aria-label="Close"
              className="btn-close"
              onClick={closeAction}
              type="button"
            />
          )}
        </div>
      </div>
    </div>
  );
}

const stepGuidance = {
  1: {
    title: "Choose what to import",
    description:
      "Select the record type that matches the data you are bringing into PIERS.",
    actions: [
      "Choose Engagement or Client import.",
      "Review what the selected import type includes.",
      "Continue when the correct record type is selected.",
    ],
  },
  2: {
    title: "Select and upload",
    description:
      "Download the approved template, enter your engagement data, and upload the completed workbook back to PIERS for validation.",
    actions: [
      "Select the import template that matches the records you want to create.",
      "Download the latest template before entering any data.",
      "Open the workbook and enter one engagement per row using the required formats and available options.",
      "Keep the worksheet names, column headings, and workbook structure unchanged, then save the file as an .xlsx workbook.",
      "Upload the completed workbook and select Validate & Continue to check the imported data.",
    ],
  },
  3: {
    title: "Resolve imported data",
    description:
      "Review every record and resolve blocking errors before continuing.",
    actions: [
      "Use the status filters to find records needing attention.",
      "Open Fix/Edit Data to resolve mappings and required values.",
      "Revalidate until all included records are ready.",
    ],
  },
  4: {
    title: "Confirm and create",
    description:
      "Check the final record summary and creation options before submitting.",
    actions: [
      "Confirm the records that will be created or skipped.",
      "Review warnings and the selected creation options.",
      "Create the ready records when the summary is correct.",
    ],
  },
};

function StepHelpCard({
  currentStep,
  guidanceStep = currentStep,
  totalSteps = 4,
  ownFormat = false,
  onStartTour,
  initiallyCollapsed = false,
  liveMessage,
  importType,
}) {
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const originalGuidance = ownFormat
    ? {
        1: {
          title: "Upload your existing file",
          description:
            "Use your engagement spreadsheet or text PDF in its existing format. AI will suggest the column mappings.",
          actions: [
            "Upload an Excel, CSV or PDF file.",
            "Review the worksheet or extracted PDF table and header row.",
            "Select Analyze & Continue.",
          ],
        },
        2: {
          title: "Review mappings",
          description: "Confirm which source columns map to PIERS fields.",
          actions: [
            "Resolve missing or uncertain mappings.",
            "Review columns excluded from import.",
            "Confirm mappings and continue to Preview.",
          ],
        },
        3: {
          title: "Preview engagements",
          description: "Review and correct engagements before importing.",
          actions: [
            "Use Fix/Edit Data to update a record.",
            "Resolve or exclude rows with blocking errors.",
            "Review the data and select Import.",
          ],
        },
        4: {
          title: "Import and results",
          description:
            "Follow processing and review the engagements created by this import.",
          actions: [
            "Keep this page open during processing.",
            "Review the final counts and imported engagements.",
            "Open Import History if processing fails.",
          ],
        },
      }[currentStep]
    : stepGuidance[guidanceStep] || stepGuidance[1];

  const adapt = (text) =>
    importType === "client" ? text.replace(/engagement/g, "client") : text;
  const guidance = {
    ...originalGuidance,
    description: adapt(originalGuidance.description),
    actions: originalGuidance.actions.map(adapt),
  };

  return (
    <aside
      className={`engagement-import__step-help${
        collapsed ? " is-collapsed" : ""
      }`}
      data-import-tour="step-help"
    >
      <button
        aria-expanded={!collapsed}
        className="engagement-import__step-help-toggle"
        onClick={() => setCollapsed((value) => !value)}
        type="button"
      >
        <span>
          <i className="bx bx-bulb" />
        </span>
        <span>
          <small>
            STEP {currentStep} OF {totalSteps}
          </small>
          <b>What to do</b>
        </span>
        <i
          className={`bx ${collapsed ? "bx-chevron-down" : "bx-chevron-up"}`}
        />
      </button>
      {liveMessage && (
        <p className="engagement-import__live-guidance" role="status">
          {liveMessage}
        </p>
      )}
      {!collapsed && (
        <div className="engagement-import__step-help-body">
          <h2>{guidance.title}</h2>
          <p>{guidance.description}</p>
          <ol>
            {guidance.actions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ol>
          <Button outline onClick={onStartTour} type="button">
            <i className="bx bx-play-circle" /> Start Guided Tour
          </Button>
        </div>
      )}
    </aside>
  );
}

function ImportTypeStep({ importType, onSelect }) {
  return (
    <section className="engagement-import__surface engagement-import__type-selection">
      <div>
        <h2>1. Choose Import Type</h2>
        <p>Select the type of records you want to import into PIERS.</p>
      </div>
      <div className="engagement-import__type-options">
        {Object.entries(importTypeDetails).map(([value, details]) => {
          const selected = importType === value;
          return (
            <button
              aria-pressed={selected}
              className={selected ? "is-selected" : ""}
              key={value}
              onClick={() => onSelect(value)}
              type="button"
            >
              <i className={`bx ${details.icon}`} />
              <span>
                <b>{details.label} Import</b>
                <small>{details.description}</small>
              </span>
              <i
                className={`bx ${
                  selected ? "bx-radio-circle-marked" : "bx-radio-circle"
                }`}
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}

function StatusBadge({ value }) {
  const tone = /ignored|excluded|not supplied|read only/i.test(value)
    ? "ignored"
    : /error|required|not configured/i.test(value)
    ? "error"
    : /changed/i.test(value)
    ? "changed"
    : /warning|needs/i.test(value)
    ? "warning"
    : "success";
  return (
    <span className={`engagement-import__badge is-${tone}`}>
      {tone === "success" && <i className="bx bx-check" />}
      {tone === "changed" && <i className="bx bx-edit-alt" />}
      {tone === "warning" && <i className="bx bx-error" />}
      {tone === "error" && <i className="bx bx-error-circle" />}
      {value}
    </span>
  );
}

export function ImportTable({
  importType,
  rows,
  selectable = false,
  selectedRowIds = [],
  onToggleRow,
  onToggleAll,
  onOpen,
}) {
  const isClientImport = importType === "client";
  const entityLabel = isClientImport ? "Client" : "Engagement";
  const allRowsSelected =
    rows.length > 0 && rows.every((row) => selectedRowIds.includes(row.id));
  return (
    <div className="engagement-import__table-wrap">
      <table className="engagement-import__table">
        <thead>
          <tr>
            {selectable && (
              <th>
                <input
                  aria-label={`Select all ${entityLabel.toLowerCase()}s`}
                  checked={allRowsSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = !allRowsSelected && rows.some((row) => selectedRowIds.includes(row.id));
                  }}
                  disabled={!rows.length}
                  onChange={(event) => onToggleAll?.(rows, event.target.checked)}
                  type="checkbox"
                />
              </th>
            )}
            <th>#</th>
            <th>{entityLabel} Name</th>
            <th>{isClientImport ? "Client Code" : "Client / Firm"}</th>
            <th>{isClientImport ? "Client Type" : "Work Type"}</th>
            <th>Industry</th>
            <th>{isClientImport ? "Primary Contact" : "Partner"}</th>
            <th>{isClientImport ? "Source" : "Template"}</th>
            <th>{isClientImport ? "External ID" : "Due Date"}</th>
            <th>Validation</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              className={`${
                selectedRowIds.includes(row.id) ? "is-selected" : ""
              } ${row.ignored ? "is-ignored" : ""}`}
              key={row.id}
            >
              {selectable && (
                <td>
                  <input
                    aria-label={`Select ${row.name}`}
                    checked={selectedRowIds.includes(row.id)}
                    onChange={(event) => onToggleRow?.(row, event.target.checked)}
                    type="checkbox"
                  />
                </td>
              )}
              <td>{index + 1}</td>
              <td>
                <strong>{row.name}</strong>
              </td>
              <td>{row.client}</td>
              <td>{row.workType}</td>
              <td>{row.industry}</td>
              <td>{row.partner}</td>
              <td>
                {isClientImport
                  ? row.source?.["Source System"] || "—"
                  : row.assignedTemplate || "—"}
              </td>
              <td>
                {isClientImport
                  ? row.source?.["Source Client ID"] || "—"
                  : toDateText(row.dueDate)}
              </td>
              <td>
                <StatusBadge
                  value={row.ignored ? "Excluded" : row.validation}
                />
              </td>
              <td>
                <ActionMenu
                  label={row.name}
                  triggerClassName="engagement-import__row-action-menu"
                  actions={[
                    {
                      label: "Fix/Edit Data",
                      icon: <i className="bx bx-cog" />,
                      onSelect: () => onOpen(row),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientConfigureDrawer({
  row,
  clientTypes,
  industryTypes,
  onApply,
  onClose,
}) {
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!row) return;
    setDraft({
      workType: row.workType === "Not supplied" ? "" : row.workType,
      industry: row.industry === "—" ? "" : row.industry,
      risk: row.risk || "Low",
      isActive: row.isActive !== false,
    });
  }, [row]);

  if (!row || !draft) return null;

  const updateDraft = (field, value) =>
    setDraft((current) => ({ ...current, [field]: value }));

  return (
    <AppOffcanvas
      backdrop
      backdropClassName="engagement-import__drawer-backdrop"
      className="engagement-import__drawer"
      direction="end"
      isOpen={Boolean(row)}
      toggle={onClose}
    >
      <header>
        <div>
          <h2>Fix/Edit Data</h2>
          <b>{row.name}</b>
          <small>Excel Row {row.sourceRow}</small>
        </div>
        <button aria-label="Close client configuration" onClick={onClose}>
          <i className="bx bx-x" />
        </button>
      </header>
      <div className="engagement-import__drawer-body">
        <section>
          <h3>Imported Client</h3>
          <dl>
            <dt>Client Name</dt>
            <dd>{row.name}</dd>
            <dt>Client Code</dt>
            <dd>{row.client}</dd>
          </dl>
        </section>
        <section>
          <h3>Client Details</h3>
          <label>
            <span className="engagement-import__field-label">
              Client Type <em>*</em>
            </span>
            <Input
              onChange={(event) => updateDraft("workType", event.target.value)}
              type="select"
              value={draft.workType}
            >
              <option value="">Select client type</option>
              {clientTypes.map((item) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
            </Input>
          </label>
          <label>
            Industry
            <Input
              onChange={(event) => updateDraft("industry", event.target.value)}
              type="select"
              value={draft.industry}
            >
              <option value="">Not specified</option>
              {industryTypes.map((item) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
            </Input>
          </label>
          <label>
            Risk Level
            <Input
              onChange={(event) => updateDraft("risk", event.target.value)}
              type="select"
              value={draft.risk}
            >
              {clientBulkEditableFields
                .find((item) => item.field === "risk")
                .options.map((option) => (
                  <option key={option}>{option}</option>
                ))}
            </Input>
          </label>
          <label>
            Status
            <Input
              onChange={(event) =>
                updateDraft("isActive", event.target.value === "Active")
              }
              type="select"
              value={draft.isActive ? "Active" : "Inactive"}
            >
              <option>Active</option>
              <option>Inactive</option>
            </Input>
          </label>
        </section>
      </div>
      <footer>
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => {
            if (!draft.workType) {
              ErrorNotify("Client Type is required.");
              return;
            }
            onApply(row.id, draft);
          }}
        >
          Apply Changes
        </Button>
      </footer>
    </AppOffcanvas>
  );
}

function UploadStep({
  definitions,
  file,
  fileError,
  importType,
  onFileSelected,
  onRemoveFile,
  onSelectTemplate,
  selectedTemplate,
}) {
  const fileInput = useRef(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const templateOptions = definitions.map((definition) => ({
    value: String(definition.id),
    definition,
  }));
  const extensions = selectedTemplate?.allowedExtensions || [];
  const accepts = extensions.join(",");
  const maxSize = selectedTemplate
    ? `${Math.round(selectedTemplate.maxFileSize / 1024 / 1024)} MB`
    : "";
  const isExcel = selectedTemplate?.formatType === "XLSX";

  const chooseFile = (picked) => {
    onFileSelected(picked);
    if (fileInput.current) fileInput.current.value = "";
  };

  const download = async () => {
    const blobName = selectedTemplate?.templateStorage?.blobName;
    const url =
      selectedTemplate?.templateUrl ||
      (blobName &&
        `${BASE_URL}${DOWNLOAD_IMPORT_DEFINITION_TEMPLATE}?blobName=${encodeURIComponent(
          blobName
        )}`);
    if (!url || downloading) return;

    setDownloading(true);
    try {
      const response = await fetch(url, { credentials: "include" });
      let fileBlob;
      if (response.status === 404) {
        const headers = (selectedTemplate?.mappings || [])
          .filter((mapping) => !mapping.ignore && mapping.source)
          .map((mapping) => mapping.source);
        if (!headers.length)
          throw new Error(
            "The template file is missing and no configured columns are available to rebuild it."
          );

        const headerRow = Math.max(
          1,
          Number(selectedTemplate?.structure?.headerRow) || 1
        );
        const rows = [
          ...Array(headerRow - 1)
            .fill(null)
            .map(() => []),
          headers,
        ];
        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        worksheet["!cols"] = headers.map((header) => ({
          wch: Math.max(14, String(header).length + 3),
        }));
        const workbook = XLSX.utils.book_new();
        const worksheetName =
          String(selectedTemplate?.structure?.worksheet || "Engagements")
            .replace(/[\\/?*[\]:]/g, " ")
            .trim()
            .slice(0, 31) || "Engagements";
        XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);
        fileBlob = new Blob(
          [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
          {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }
        );
      } else {
        if (!response.ok)
          throw new Error("The template file could not be downloaded.");
        fileBlob = await response.blob();
      }

      const objectUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        selectedTemplate?.templateStorage?.fileName || "import-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      SuccessNotify("Template download started.");
    } catch (error) {
      ErrorNotify(
        error.message || "The template file could not be downloaded."
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="engagement-import__upload">
      <section className="engagement-import__surface engagement-import__template-upload">
        <header>
          <h2>Select Import Template</h2>
          <p>
            Choose the import definition that matches the structure of your
            source data.
          </p>
        </header>
        <div className="engagement-import__template-choice-layout">
          <label className="engagement-import__template-selector">
            <span>
              Import Template <b>*</b>
            </span>
            <Select
              aria-label="Import Template"
              className="engagement-import__template-select"
              classNamePrefix="template-select"
              formatOptionLabel={(option) => (
                <span className="engagement-import__template-option">
                  <span>{option.definition.name}</span>
                  <small>v{templateVersionLabel(option.definition)}</small>
                </span>
              )}
              getOptionLabel={(option) => option.definition.name}
              getOptionValue={(option) => option.value}
              isClearable
              isSearchable
              noOptionsMessage={() => "No import templates found"}
              onChange={(option) =>
                onSelectTemplate(option?.definition || null)
              }
              options={templateOptions}
              placeholder="Search or select an import template..."
              value={
                templateOptions.find(
                  (option) =>
                    String(option.value) === String(selectedTemplate?.id)
                ) || null
              }
            />
          </label>
          {selectedTemplate && (
            <article className="engagement-import__selected-template">
              <header>
                <div>
                  <h3>{selectedTemplate.name}</h3>
                  {selectedTemplate.isRecommended && <span>Recommended</span>}
                </div>
                <p>
                  {selectedTemplate.description ||
                    "Configured import definition."}
                </p>
              </header>
              <div className="engagement-import__template-metadata">
                <span>
                  <b>Source</b>
                  {selectedTemplate.sourceSystem}
                </span>
                <span>
                  <b>Format</b>
                  {formatLabel(selectedTemplate.formatType)}
                </span>
                <span>
                  <b>Version</b>
                  {templateVersionLabel(selectedTemplate)}
                </span>
              </div>
              <p className="engagement-import__template-contents">
                Includes:{" "}
                {[
                  selectedTemplate.structure?.worksheet,
                  ...(selectedTemplate.structure?.optionalSheets || []),
                ]
                  .filter(Boolean)
                  .join(" • ") || "Configured fields and validation rules"}
              </p>
              <footer>
                <Button outline onClick={() => setDetailsOpen(true)}>
                  View Template Details
                </Button>
                {selectedTemplate.templateUrl ||
                selectedTemplate.templateStorage?.blobName ? (
                  <Button
                    outline
                    type="button"
                    disabled={downloading}
                    onClick={download}
                  >
                    <i
                      className={`bx ${
                        downloading ? "bx-loader-alt bx-spin" : "bx-download"
                      }`}
                    />{" "}
                    {downloading ? "Downloading…" : "Download Template"}
                  </Button>
                ) : (
                  <span>
                    {selectedTemplate.instructions ||
                      `Upload the corresponding ${selectedTemplate.sourceSystem} export file.`}
                  </span>
                )}
              </footer>
            </article>
          )}
        </div>
        <section className="engagement-import__upload-source">
          <h2>Upload Source File</h2>
          <p>
            {selectedTemplate
              ? `Upload ${
                  isExcel ? "an Excel file" : "a file"
                } that matches the selected ${
                  selectedTemplate.name
                } import definition.`
              : "Select an import template before uploading a source file."}
          </p>
          {file ? (
            <div className="engagement-import__file">
              <i className={`bx ${isExcel ? "bxs-file-xls" : "bx-file"}`} />
              <div>
                <b>{file.name}</b>
                <span>
                  {(file.size / 1024 / 1024).toFixed(1)} MB ·{" "}
                  {formatLabel(selectedTemplate?.formatType)}
                </span>
                <small>
                  <i className="bx bx-check" /> File uploaded successfully
                </small>
              </div>
              <Button outline onClick={() => fileInput.current?.click()}>
                Replace File
              </Button>
              <Button outline onClick={onRemoveFile}>
                Remove
              </Button>
            </div>
          ) : (
            <button
              className="engagement-import__dropzone"
              disabled={!selectedTemplate}
              onClick={() => fileInput.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                chooseFile(event.dataTransfer.files?.[0] || null);
              }}
              type="button"
            >
              <i className="bx bx-cloud-upload" />
              <b>
                Drag and drop your {isExcel ? "Excel file" : "source file"} here
              </b>
              <span>or</span>
              <em>Browse Files</em>
              <small>
                {selectedTemplate
                  ? `${extensions.join(" • ")} • Maximum file size ${maxSize}`
                  : "Select an import template before uploading a source file."}
              </small>
            </button>
          )}
          {fileError && (
            <p className="engagement-import__upload-error">
              <i className="bx bx-error-circle" /> {fileError}
            </p>
          )}
          <input
            accept={accepts}
            hidden
            onChange={(event) => chooseFile(event.target.files?.[0] || null)}
            ref={fileInput}
            type="file"
          />
        </section>
      </section>
      <AppModal
        fullscreen
        isOpen={detailsOpen}
        toggle={() => setDetailsOpen(false)}
        modalClassName="import-definition-view-modal"
        contentClassName="import-definition-view-modal__content"
        aria-label="Import definition preview"
      >
        <ImportDefinitionViewPage
          definition={selectedTemplate}
          definitionId={selectedTemplate?.id}
          onClose={() => setDetailsOpen(false)}
          modal
        />
      </AppModal>
    </div>
  );
}

export default function DataImportWizardPage({
  closeAction,
  embeddedHeader,
  initialImportType,
  initialInspectionCycleId = 0,
  showImportTypeSelection = false,
  onImportStarted,
  onBusyChange,
  presentation,
  cycle,
  hidePageHeader = false,
  embedded = false,
}) {
  const navigate = useNavigate();
  const { registerContext } = useContextualHelp();
  const isPage = embedded || presentation === "page" || !closeAction;
  const pageClassName = `page-content engagement-import${
    isPage ? " engagement-import--page" : " engagement-import--modal"
  }${embedded ? " engagement-import--embedded" : ""}`;
  const [step, setStep] = useState(1);
  const [importType, setImportType] = useState(
    initialImportType || (showImportTypeSelection ? null : "engagement")
  );
  const [choosingImportType, setChoosingImportType] = useState(
    showImportTypeSelection && !initialImportType
  );
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [definitions, setDefinitions] = useState([]);
  const [selectedImportTemplate, setSelectedImportTemplate] = useState(null);
  const [pendingImportTemplate, setPendingImportTemplate] = useState(null);
  const [rows, setRows] = useState([]);
  const deletedSourceRows = useRef(new Set());
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [validationFilter, setValidationFilter] = useState("all");
  const [validationSearch, setValidationSearch] = useState("");
  const [clientTypes, setClientTypes] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [reviewTemplates, setReviewTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkField, setBulkField] = useState(null);
  const [bulkValue, setBulkValue] = useState("");
  const [bulkOverwrite, setBulkOverwrite] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [newClientDrawerOpen, setNewClientDrawerOpen] = useState(false);
  const newClientCallback = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [structureNotice, setStructureNotice] = useState("");
  const [headingReviewConfirmed, setHeadingReviewConfirmed] = useState(false);
  const [dateOrder, setDateOrder] = useState("");
  const [originalWorkbook, setOriginalWorkbook] = useState(null);
  const [sourceWorkbook, setSourceWorkbook] = useState(null);
  const [sourceSheet, setSourceSheet] = useState("");
  const [sourceHeaderRow, setSourceHeaderRow] = useState(1);
  const sourceMatrix = useMemo(() => sourceWorkbook?.Sheets[sourceSheet]
    ? XLSX.utils.sheet_to_json(sourceWorkbook.Sheets[sourceSheet],
      { header: 1, defval: "", raw: true, range: 0 })
    : [], [sourceWorkbook, sourceSheet]);
  const headerRowError = file ? validateHeaderRow(sourceMatrix, sourceHeaderRow) : "";
  const [columnMappings, setColumnMappings] = useState({});
  const [mappingHeaders, setMappingHeaders] = useState([]);
  const [mappingWarnings, setMappingWarnings] = useState([]);
  const [aiUsed, setAiUsed] = useState(false);
  const [mappingConfirmed, setMappingConfirmed] = useState(false);
  const [dataStepReached, setDataStepReached] = useState(false);
  const [importError, setImportError] = useState("");
  const [resultSearch, setResultSearch] = useState("");
  const initialMappings = useRef({});
  const mappingSource = useRef(null);
  const mappingSamples = useMemo(() => {
    if (!sourceWorkbook?.Sheets[sourceSheet]) return {};
    const matrix = XLSX.utils.sheet_to_json(
      sourceWorkbook.Sheets[sourceSheet],
      { header: 1, defval: "", raw: false, range: 0 }
    );
    const samples = Object.fromEntries(
      mappingHeaders.map((header, index) => [
        header,
        matrix
          .slice(sourceHeaderRow, sourceHeaderRow + 5)
          .map((row) => row[index])
          .find(
            (value) => value !== null && value !== undefined && value !== ""
          ) ?? "",
      ])
    );
    for (const key of ["dueDate", "financialYearEnd"]) {
      const header = columnMappings[key];
      if (header && samples[header] !== undefined) samples[header] = normalizeImportDate(samples[header], { date1904: !!sourceWorkbook.Workbook?.WBProps?.date1904, dateOrder }) || samples[header];
    }
    return samples;
  }, [sourceWorkbook, sourceSheet, sourceHeaderRow, mappingHeaders, columnMappings, dateOrder]);
  useEffect(() => {
    onBusyChange?.(creating);
    return () => onBusyChange?.(false);
  }, [creating, onBusyChange]);

  const resetImport = () => {
    setFile(null);
    setFileError("");
    setRows([]);
    deletedSourceRows.current.clear();
    setSelectedRowIds([]);
    setValidationFilter("all");
    setValidationSearch("");
    setDrawer(null);
    setImportReport(null);
    setSourceWorkbook(null);
    setColumnMappings({});
    setMappingHeaders([]);
    setMappingConfirmed(false);
    setDataStepReached(false);
    setImportError("");
    setResultSearch("");
    setStep(1);
  };
  const leaveImport =
    closeAction ||
    (embedded
      ? resetImport
      : () =>
          navigate(
            importType === "client" ? "/clients/list" : "/engagements/list"
          ));
  useUnsavedChanges(Boolean(isPage && file && !importReport));

  const requestExit = () => {
    if (creating || parsing) return false;
    return (
      importReport ||
      !file ||
      window.confirm(
        "Discard this unfinished import and leave? Your uploaded file and corrections will be lost."
      )
    );
  };

  const exitImport = () => {
    if (requestExit()) leaveImport();
  };

  useEffect(() => {
    if (!isPage || !file) return undefined;
    const historyIndex = window.history.state?.idx;
    let restoringHistory = false;

    const onPopState = (event) => {
      if (restoringHistory) {
        restoringHistory = false;
        event.stopImmediatePropagation();
        return;
      }
      const destinationIndex = event.state?.idx;
      if (
        typeof historyIndex !== "number" ||
        typeof destinationIndex !== "number" ||
        historyIndex === destinationIndex
      )
        return;
      if (
        creating ||
        parsing ||
        !window.confirm(
          "Discard this unfinished import and leave? Your uploaded file and corrections will be lost."
        )
      ) {
        event.stopImmediatePropagation();
        restoringHistory = true;
        window.history.go(historyIndex - destinationIndex);
      }
    };

    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    const onNavigation = (event) => {
      const link = event.target.closest?.("a[href]");
      if (
        !link ||
        link.target === "_blank" ||
        link.hasAttribute("download") ||
        link.getAttribute("href").startsWith("#")
      )
        return;
      if (
        creating ||
        parsing ||
        !window.confirm(
          "Discard this unfinished import and leave? Your uploaded file and corrections will be lost."
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("popstate", onPopState, true);
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", onNavigation, true);
    return () => {
      window.removeEventListener("popstate", onPopState, true);
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", onNavigation, true);
    };
  }, [isPage, file, creating, parsing]);

  const isClientImport = importType === "client";
  const compactEngagementFlow = importType === "engagement";
  const currentStep = compactEngagementFlow
    ? step
    : choosingImportType
    ? 1
    : step + 1;
  const maxReachableStep =
    compactEngagementFlow && (creating || importReport || step === 4)
      ? 4
      : compactEngagementFlow && dataStepReached
      ? 3
      : compactEngagementFlow && rows.length
      ? 2
      : currentStep;

  useEffect(() => {
    if (isPage || !closeAction || typeof document === "undefined")
      return undefined;
    document.documentElement.classList.add("engagement-import-modal-open");
    return () => {
      document.documentElement.classList.remove("engagement-import-modal-open");
    };
  }, [closeAction, isPage]);

  useEffect(() => {
    if (importType !== "engagement") return undefined;
    registerContext("data.import-engagements");
    return () => registerContext(null);
  }, [importType, registerContext]);

  const selectImportType = (type) => {
    if (type !== importType) {
      setFile(null);
      setFileError("");
      setSelectedImportTemplate(null);
      setRows([]);
      deletedSourceRows.current.clear();
      setSelectedRowIds([]);
      setDrawer(null);
    }
    setImportType(type);
    setStep(1);
  };

  useEffect(() => {
    if (!importType || importType === "engagement") return;
    let mounted = true;

    const loadDefinitions = async () => {
      let configured = [];
      try {
        const response = await getData(
          `${GET_IMPORT_DEFINITION_LIST}?target=${encodeURIComponent(
            importTargetFor(importType)
          )}`
        );
        configured = response?.status ? response.data || [] : [];
      } catch {
        configured = [];
      }
      const target = importTargetFor(importType);
      const active = configured
        .map(normaliseDefinition)
        .filter(
          (item) =>
            item.target === target &&
            String(item.status).toLowerCase() === "active"
        );
      const available = active;
      if (!mounted) return;
      setDefinitions(available);
      setSelectedImportTemplate(
        (current) =>
          (current &&
            available.find((item) => String(item.id) === String(current.id))) ||
          available.find((item) => item.isDefault) ||
          available[0] ||
          null
      );
    };
    loadDefinitions();
    return () => {
      mounted = false;
    };
  }, [importType]);

  useEffect(() => {
    if (importType !== "client") return;
    let mounted = true;
    Promise.all([getClientTypes(), getIndustryTypes()])
      .then(([typesResponse, industriesResponse]) => {
        if (!mounted) return;
        setClientTypes(typesResponse?.status ? typesResponse.data || [] : []);
        setIndustryTypes(
          industriesResponse?.status ? industriesResponse.data || [] : []
        );
      })
      .catch(() => {
        if (!mounted) return;
        setClientTypes([]);
        setIndustryTypes([]);
      });
    return () => {
      mounted = false;
    };
  }, [importType]);

  useEffect(() => {
    if (importType !== "engagement") {
      setReviewTemplates([]);
      setClients([]);
      setUsers([]);
      setWorkTypes([]);
      return;
    }
    let mounted = true;
    Promise.all([
      getReviewTemplateList(),
      searchClients(),
      getClientTypes(),
      getIndustryTypes(),
      getUsersList(),
      getWorkTypes(),
    ])
      .then(
        ([
          templatesResponse,
          clientsResponse,
          clientTypesResponse,
          industryTypesResponse,
          usersResponse,
          workTypesResponse,
        ]) => {
          if (!mounted) return;
          setReviewTemplates(
            templatesResponse?.status ? templatesResponse.data || [] : []
          );
          setClients(clientsResponse?.status ? clientsResponse.data || [] : []);
          setClientTypes(
            clientTypesResponse?.status ? clientTypesResponse.data || [] : []
          );
          setIndustryTypes(
            industryTypesResponse?.status
              ? industryTypesResponse.data || []
              : []
          );
          setUsers(
            usersResponse?.status
              ? (usersResponse.data || []).filter(
                  (user) => user.status === 1 && user.isBlocked !== 1
                )
              : []
          );
          setWorkTypes(
            workTypesResponse?.status ? workTypesResponse.data || [] : []
          );
        }
      )
      .catch(() => {
        if (!mounted) return;
        setReviewTemplates([]);
        setClients([]);
        setClientTypes([]);
        setIndustryTypes([]);
        setUsers([]);
        setWorkTypes([]);
      });
    return () => {
      mounted = false;
    };
  }, [importType]);

  useEffect(() => {
    if (importType !== "engagement") return;
    setRows((current) =>
      current.map((row) =>
        row.validationIssues?.length
          ? row
          : resolveEngagementClient(row, clients)
      )
    );
    setDrawer((current) =>
      current ? resolveEngagementClient(current, clients) : current
    );
  }, [clients, importType]);

  const validateSelectedFile = async (
    selectedFile,
    template = selectedImportTemplate
  ) => {
    setFileError("");
    if (!selectedFile || !template) return;
    const extension = `.${
      selectedFile.name.split(".").pop()?.toLowerCase() || ""
    }`;
    if (
      !template.allowedExtensions
        .map((item) => item.toLowerCase())
        .includes(extension)
    ) {
      setFile(null);
      setFileError(
        `This template accepts ${template.allowedExtensions.join(
          ", "
        )} files only.`
      );
      return;
    }
    if (!selectedFile.size) {
      setFile(null);
      setFileError("The selected file is empty.");
      return;
    }
    if (
      template.formatType === "XLSX" &&
      selectedFile.type &&
      ![
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/octet-stream",
      ].includes(selectedFile.type)
    ) {
      setFile(null);
      setFileError("This template accepts a valid Excel (.xlsx) file only.");
      return;
    }
    if (selectedFile.size > template.maxFileSize) {
      setFile(null);
      setFileError(
        `The selected file exceeds the ${Math.round(
          template.maxFileSize / 1024 / 1024
        )} MB maximum size.`
      );
      return;
    }
    try {
      if (template.formatType === "XLSX") {
        const workbook = XLSX.read(await selectedFile.arrayBuffer(), {
          type: "array",
        });
        if (!workbook.SheetNames.length)
          throw new Error("The workbook has no worksheets.");
        const requiredSheet = template.structure?.worksheet;
        if (
          requiredSheet &&
          !workbook.SheetNames.some(
            (name) => name.toLowerCase() === requiredSheet.toLowerCase()
          )
        )
          throw new Error(
            `This template requires the ${requiredSheet} worksheet.`
          );
      }
      setFile(selectedFile);
    } catch (error) {
      setFile(null);
      setFileError(error.message || "PIERS could not read the selected file.");
    }
  };

  const selectImportTemplate = (template) => {
    if (file && String(template?.id) !== String(selectedImportTemplate?.id)) {
      setPendingImportTemplate({ template });
      return;
    }
    if (file && String(template?.id) !== String(selectedImportTemplate?.id)) {
      setFile(null);
      setFileError("");
      setRows([]);
      deletedSourceRows.current.clear();
      setSelectedRowIds([]);
      setDrawer(null);
    }
    setSelectedImportTemplate(template);
  };

  const confirmImportTemplateChange = () => {
    setFile(null);
    setFileError("");
    setRows([]);
    deletedSourceRows.current.clear();
    setSelectedRowIds([]);
    setDrawer(null);
    setSelectedImportTemplate(pendingImportTemplate?.template || null);
    setPendingImportTemplate(null);
  };

  const selectStepperStep = (displayStep) => {
    if (compactEngagementFlow) {
      setChoosingImportType(false);
      if (creating || importReport) return;
      if (displayStep === 1) {
        setMappingConfirmed(false);
        setDataStepReached(false);
      }
      setStep(displayStep);
      return;
    }
    if (displayStep === 1) {
      setChoosingImportType(true);
      return;
    }
    setChoosingImportType(false);
    setStep(displayStep - 1);
  };

  const startCurrentStepTour = () => {
    if (compactEngagementFlow) {
      startGuidedTour([
        {
          target: ".engagement-import__stepper",
          title: "Four steps",
          description:
            "Upload your file, confirm mappings, update the engagement data, then follow the import and its results.",
        },
        {
          target:
            step === 1
              ? ".engagement-import__own-upload"
              : step === 2
              ? ".engagement-import__mapping-review"
              : ".engagement-import__step-content",
          title: step === 1 ? "Use your existing format" : "Review your import",
          description:
            step === 1
              ? "Upload Excel, CSV or a text PDF, then review the source table and header row."
              : "Review mappings and resolve errors before creating engagements.",
        },
      ]);
      return;
    }
    const tours = {
      1: [
        {
          target: ".engagement-import__stepper",
          title: "Import progress",
          description:
            "This progress navigation shows where you are and lets you return to completed steps.",
        },
        {
          target: ".engagement-import__type-options",
          title: "Choose an import type",
          description: "Select the kind of PIERS records you want to import.",
        },
        {
          target: ".engagement-import__modal-footer",
          title: "Continue",
          description: "Continue after selecting the correct record type.",
        },
      ],
      2: [
        {
          target: ".engagement-import__template-selector",
          title: "Select a template",
          description:
            "Choose the approved import definition that matches your source data.",
        },
        {
          target: ".engagement-import__selected-template",
          title: "Review template details",
          description:
            "Confirm the source, format, version, and included data before uploading.",
        },
        {
          target: ".engagement-import__upload-source",
          title: "Upload your source file",
          description:
            "Add a file that follows the selected template, then validate it.",
        },
        {
          target: ".engagement-import__modal-footer",
          title: "Validate the workbook",
          description:
            "PIERS checks the uploaded records before moving to review and configuration.",
        },
      ],
      3: [
        {
          target: ".engagement-import__file-summary",
          title: "Validation summary",
          description:
            "Review the totals for ready records, warnings, and blocking errors.",
        },
        {
          target: ".engagement-import__toolbar",
          title: "Find records",
          description:
            "Filter or search the imported records to focus on items needing attention.",
        },
        {
          target: ".engagement-import__table-wrap",
          title: "Review and resolve",
          description:
            "Open a record to correct mappings and required values, then revalidate.",
        },
        {
          target: ".engagement-import__modal-footer",
          title: "Continue when ready",
          description:
            "Blocking errors must be resolved before you can review and create records.",
        },
      ],
      4: [
        {
          target: ".engagement-import__review-summary",
          title: "Confirm the summary",
          description:
            "Check how many records are ready and review the import totals.",
        },
        {
          target: ".engagement-import__review-grid",
          title: "Review creation details",
          description:
            "Confirm the included records, warnings, and creation options.",
        },
        {
          target: ".engagement-import__modal-footer",
          title: "Create records",
          description:
            "Submit the ready records when the final review is correct.",
        },
      ],
    };
    const compactTours = {
      1: [
        ...tours[2].slice(0, 3),
        {
          target: ".engagement-import__inline-actions",
          title: "Validate the workbook",
          description:
            "PIERS checks the uploaded records before moving to validation and creation.",
        },
      ],
      2: [
        ...tours[3].slice(0, 3),
        {
          target: ".engagement-import__file-summary-actions",
          title: "Create records",
          description:
            "When blocking errors are resolved, start creation for the included engagements.",
        },
      ],
    };
    window.setTimeout(
      () =>
        startGuidedTour(
          (compactEngagementFlow ? compactTours : tours)[currentStep] ||
            tours[1]
        ),
      0
    );
  };

  const counts = useMemo(
    () => ({
      total: rows.length,
      ready: rows.filter((row) => !row.ignored && row.validation === "Ready")
        .length,
      warning: rows.filter(
        (row) => !row.ignored && row.validation === "Warning"
      ).length,
      error: rows.filter((row) => !row.ignored && row.validation === "Error")
        .length,
      excluded: rows.filter((row) => row.ignored).length,
      configured: rows.filter((row) => !row.ignored && row.assignedTemplate)
        .length,
    }),
    [rows]
  );
  const teamMembersSupplied = useMemo(
    () =>
      rows.reduce((total, row) => {
        if (row.ignored) return total;
        return (
          total +
          [row.partner, row.manager].filter(
            (member) => member && member !== "—"
          ).length
        );
      }, 0),
    [rows]
  );

  const validateClientImportRow = (row) => {
    const validationIssues = [];
    const isMissingClientName =
      !row.name || row.name.startsWith("Unnamed client");
    const isMissingClientCode =
      !row.client || row.client === "Client code not supplied";
    const isMissingClientType =
      !row.workType || row.workType === "Not supplied";
    const isMissingPrimaryContact = !row.partner || row.partner === "—";
    const isMissingPrimaryContactEmail = !row.primaryContactEmail;
    if (isMissingClientName)
      validationIssues.push({
        field: "Client Name",
        message: "Enter a client name.",
      });
    if (isMissingClientCode)
      validationIssues.push({
        field: "Client Code",
        message: "Enter a client code.",
      });
    if (isMissingClientType)
      validationIssues.push({
        field: "Client Type",
        message: "Select a client type.",
      });
    if (isMissingPrimaryContact)
      validationIssues.push({
        field: "Primary Contact Name",
        message: "Enter the primary contact name in the workbook.",
      });
    if (isMissingPrimaryContactEmail)
      validationIssues.push({
        field: "Primary Contact Email",
        message: "Enter the primary contact email in the workbook.",
      });
    const hasKnownClientType =
      !clientTypes.length ||
      clientTypes.some(
        (item) => item.name.toLowerCase() === row.workType.toLowerCase()
      );
    const hasKnownIndustry =
      !row.industry ||
      row.industry === "—" ||
      !industryTypes.length ||
      industryTypes.some(
        (item) => item.name.toLowerCase() === row.industry.toLowerCase()
      );
    const hasAllowedRisk = ["Low", "Medium", "High"].includes(row.risk);
    const hasValidPrimaryContactEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      row.primaryContactEmail
    );
    if (!isMissingClientType && !hasKnownClientType)
      validationIssues.push({
        field: "Client Type",
        message:
          "The imported client type is unavailable. Select a valid client type.",
      });
    if (!hasKnownIndustry)
      validationIssues.push({
        field: "Industry",
        message:
          "The imported industry is unavailable. Select a valid industry or leave it blank.",
      });
    if (!hasAllowedRisk)
      validationIssues.push({
        field: "Risk Level",
        message: "Select Low, Medium, or High.",
      });
    if (!isMissingPrimaryContactEmail && !hasValidPrimaryContactEmail)
      validationIssues.push({
        field: "Primary Contact Email",
        message: "Enter a valid primary contact email in the workbook.",
      });
    const valid = validationIssues.length === 0;
    return {
      ...row,
      validationIssues,
      clientResolution: valid
        ? "Ready for client creation"
        : `${validationIssues.length} field${
            validationIssues.length === 1 ? "" : "s"
          } require attention`,
      included: !row.ignored && valid,
      validation: valid ? "Ready" : "Error",
    };
  };

  const applyClientChanges = (id, changes) =>
    setRows((current) =>
      current.map((row) => {
        if (row.id !== id) return row;
        const changedAt = new Date().toISOString();
        const overrides = Object.entries(changes).reduce(
          (currentOverrides, [field, value]) => {
            if (row[field] === value) return currentOverrides;
            return {
              ...currentOverrides,
              [field]: {
                changedAt,
                configuredValue: value,
                originalValue:
                  row.overrides?.[field]?.originalValue ?? row[field],
              },
            };
          },
          row.overrides || {}
        );
        return validateClientImportRow({ ...row, ...changes, overrides });
      })
    );
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedRowIds.includes(row.id)),
    [rows, selectedRowIds]
  );
  const selectedClientRows = selectedRows;
  const bulkOptions = useMemo(() => {
    if (!bulkField) return [];
    if (bulkField.lookup === "clientTypes")
      return clientTypes.map((item) => item.name);
    if (bulkField.lookup === "industryTypes")
      return industryTypes.map((item) => item.name);
    return bulkField.options || [];
  }, [bulkField, clientTypes, industryTypes]);

  const applyBulkClientValue = () => {
    if (!bulkField || !selectedClientRows.length) return;
    if (bulkField.required && !bulkValue) {
      ErrorNotify(`${bulkField.label} is required.`);
      return;
    }
    const configuredValue =
      bulkField.field === "isActive" ? bulkValue === "Active" : bulkValue;

    const isEmpty = (value) =>
      value === undefined ||
      value === null ||
      value === "" ||
      value === "—" ||
      value === "Not supplied";
    const selectedIds = new Set(selectedRowIds);
    setRows((current) =>
      current.map((row) => {
        if (
          !selectedIds.has(row.id) ||
          (!bulkOverwrite && !isEmpty(row[bulkField.field]))
        )
          return row;
        const changedAt = new Date().toISOString();
        return validateClientImportRow({
          ...row,
          [bulkField.field]: configuredValue,
          overrides: {
            ...(row.overrides || {}),
            [bulkField.field]: {
              changedAt,
              configuredValue,
              originalValue:
                row.overrides?.[bulkField.field]?.originalValue ??
                row[bulkField.field],
            },
          },
        });
      })
    );
    setBulkField(null);
    setBulkMenuOpen(false);
  };

  const toggleSelectedImportInclusion = () => {
    if (!selectedRows.length) {
      ErrorNotify("Select at least one row to update.");
      return;
    }
    const shouldInclude = selectedRows.every((row) => row.ignored);
    const selectedIds = new Set(selectedRowIds);

    setRows((current) => {
      const changed = current.map((row) =>
        selectedIds.has(row.id) ? { ...row, ignored: !shouldInclude } : row
      );
      return compactEngagementFlow
        ? validateOwnRows(changed)
        : changed.map(validateClientImportRow);
    });
    setBulkMenuOpen(false);
  };

  const deleteSelectedRows = () => {
    if (!selectedRows.length) {
      ErrorNotify("Select at least one row to delete.");
      return;
    }
    const selectedIds = new Set(selectedRowIds);
    selectedRows.forEach(row => deletedSourceRows.current.add(row.sourceRow));
    setRows((current) => {
      const remaining = current.filter((row) => !selectedIds.has(row.id));
      return compactEngagementFlow ? validateOwnRows(remaining) : remaining;
    });
    setSelectedRowIds([]);
    setDrawer((current) =>
      current && selectedIds.has(current.id) ? null : current
    );
    setBulkMenuOpen(false);
    SuccessNotify(
      `${selectedRows.length} ${isClientImport ? "client" : "engagement"}${
        selectedRows.length === 1 ? "" : "s"
      } removed from this import.`
    );
  };

  const revalidateCurrentRows = () => {
    setRows((current) =>
      compactEngagementFlow
        ? validateOwnRows(current)
        : current.map((row) => validateClientImportRow(row))
    );
    SuccessNotify("Current import changes have been revalidated.");
  };
  const validatedRows = useMemo(() => {
    const query = validationSearch.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesFilter =
        validationFilter === "all" ||
        (validationFilter === "excluded" && row.ignored) ||
        (!row.ignored && row.validation.toLowerCase() === validationFilter);
      const matchesSearch =
        !query ||
        [row.name, row.client, row.workType, row.industry, row.partner]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      return matchesFilter && matchesSearch;
    });
  }, [rows, validationFilter, validationSearch]);

  const toggleRowSelection = (row, checked) =>
    setSelectedRowIds((current) =>
      checked
        ? [...new Set([...current, row.id])]
        : current.filter((id) => id !== row.id)
    );

  const toggleAllRowSelection = (visibleRows, checked) =>
    setSelectedRowIds((current) => {
      const visibleIds = visibleRows.map((row) => row.id);
      return checked
        ? [...new Set([...current, ...visibleIds])]
        : current.filter((id) => !visibleIds.includes(id));
    });

  const openNewClientDrawer = (onCreated) => {
    newClientCallback.current = onCreated;
    setNewClientDrawerOpen(true);
  };

  const closeNewClientDrawer = () => {
    newClientCallback.current = null;
    setNewClientDrawerOpen(false);
  };

  const selectNewClient = (client) => {
    setClients((current) => [
      client,
      ...current.filter((item) => String(item.id) !== String(client.id)),
    ]);
    newClientCallback.current?.(client);
    newClientCallback.current = null;
  };

  const applyEngagementChanges = (id, changes) => {
    setRows((current) => {
      const changed = current.map((row) =>
        row.id === id
          ? { ...row, ...changes, overrides: changes.overrides || {} }
          : row
      );
      return compactEngagementFlow
        ? validateOwnRows(changed)
        : changed.map(validateEngagementImportRow);
    });
  };

  const readOwnFile = async (selected) => {
    setFileError("");
    setRows([]);
    deletedSourceRows.current.clear();
    setDataStepReached(false);
    setMappingConfirmed(false);
    setSourceWorkbook(null);
    setStructureNotice("");
    setHeadingReviewConfirmed(false);
    setDateOrder("");
    setOriginalWorkbook(null);
    setFile(null);
    if (!selected) return;
    if (
      !/\.(xlsx|csv|pdf)$/i.test(selected.name) ||
      !selected.size ||
      selected.size > 10 * 1024 * 1024
    ) {
      setFileError("Choose an Excel (.xlsx), CSV or PDF file up to 10 MB.");
      return;
    }
    setParsing(true);
    try {
      let workbook;
      if (/\.pdf$/i.test(selected.name)) {
        const data = new FormData();
        data.append("importFile", selected);
        const response = await postRawData("/dataimport/extract-engagement-pdf", "POST", data);
        if (!response.status) throw new Error(response.message || "Unable to extract the PDF table.");
        const matrix = response.data?.rows;
        if (!Array.isArray(matrix) || matrix.length < 2) throw new Error("No engagement table was found in this PDF.");
        workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(matrix), "PDF table");
      } else {
        workbook = readExcelWorkbook(await selected.arrayBuffer());
        setOriginalWorkbook(workbook);
        try {
          const response = await postData("/dataimport/detect-excel-structure", excelStructureSamples(workbook));
          if (!response?.status) throw new Error(response?.message || "AI structure detection is unavailable.");
          let detected = response.data;
          const normalized = normalizeExcelStructure(workbook, detected);
          detected = normalized.piersStructure;
          const matrix = XLSX.utils.sheet_to_json(normalized.Sheets[detected.sheet], { header: 1, defval: "", raw: true });
          const error = validateHeaderRow(matrix, 1);
          if (error) throw new Error(error);
          setStructureNotice(`AI selected worksheet “${detected.sheet}”, ${detected.orientation === "columns" ? "records across columns (transposed for review)" : "records in rows"}, headings ${detected.headerStart}–${detected.headerEnd}. The table below is normalized: headings are row 1. Review the sample values before continuing.`);
          workbook = normalized;
        } catch (error) {
          setStructureNotice(`${error.message || "AI structure detection is unavailable."} Original worksheets are shown below; select the worksheet and headings manually. Complex layouts must be flattened before continuing.`);
        }
      }
      if (!workbook.SheetNames.length) throw new Error();
      setSourceWorkbook(workbook);
      setSourceSheet(workbook.SheetNames[0]);
      setSourceHeaderRow(workbook.piersStructure ? 1 : suggestHeaderRow(XLSX.utils.sheet_to_json(
        workbook.Sheets[workbook.SheetNames[0]],
        { header: 1, defval: "", raw: true, range: 0 }
      )));
      setFile(selected);
    } catch (error) {
      setFileError(
        error.message
          ? error.message
          : "PIERS could not read this file. Upload a valid Excel, CSV or PDF file."
      );
    } finally {
      setParsing(false);
    }
  };

  const rebuildMappedRows = (mappings, previousRows = [], preserveCorrections = false) => {
    const matrix = XLSX.utils.sheet_to_json(
      sourceWorkbook.Sheets[sourceSheet],
      { header: 1, defval: "", raw: true, range: 0 }
    );
    const sources = mappedSourceRows(matrix, sourceHeaderRow, mappings);
    const detected = sourceWorkbook.piersStructure;
    if (detected) sources.forEach(source => {
      const originalIndex = detected.headerEnd + source.__sourceRow - 1;
      source.__sourceLocation = `${detected.sheet}: ${detected.orientation === "columns" ? `Excel column ${XLSX.utils.encode_col(originalIndex - 1)}` : `Excel row ${originalIndex}`}`;
    });
    if (!sources.length || sources.length > 10000)
      throw new Error("Upload between 1 and 10,000 engagement rows.");
    const excludedSourceRows = new Set(previousRows.filter(row => row.ignored).map(row => row.sourceRow));
    const previousBySource = new Map(previousRows.map(row => [row.sourceRow, row]));
    return validateOwnRows(
      normalizeRows(sources, "engagement", {
        dataStartRow: sourceHeaderRow + 1,
        date1904: !!sourceWorkbook.Workbook?.WBProps?.date1904,
        dateOrder,
      }).filter(row => !deletedSourceRows.current.has(row.sourceRow))
        .map(row => {
          const overrides = preserveCorrections ? previousBySource.get(row.sourceRow)?.overrides || {} : {};
          return {
            ...row,
            ...Object.fromEntries(Object.entries(overrides).map(([field, correction]) => [field, correction.configuredValue])),
            overrides,
            ignored: excludedSourceRows.has(row.sourceRow),
          };
        })
    );
  };

  const validateOwnRows = (inputRows) => {
    const keys = new Set();
    return inputRows.map((row) => {
      const resolved = resolveEngagementClient(row, clients);
      const issues = [...resolved.validationIssues];
      const warnings = [];
      if (
        (!row.partner && !row.manager) ||
        (row.partner === "—" && row.manager === "—")
      )
        warnings.push({
          field: "Team",
          message: "No engagement partner or manager supplied.",
        });
      if (resolved.createClient && !clientTypes.length)
        issues.push({
          field: "Client / Firm",
          message:
            "Configure an active client type before creating new clients.",
        });
      if (!row.client && !row.clientCode)
        issues.push({
          field: "Client",
          message: "Supply a client name or code; PIERS will not invent one.",
        });
      if (
        !workTypes.some(
          (item) =>
            item.name.toLowerCase() ===
            String(row.workType).trim().toLowerCase()
        )
      )
        issues.push({
          field: "Work Type",
          message: "Select an active PIERS work type.",
        });
      if (
        row.industry &&
        row.industry !== "—" &&
        !industryTypes.some(
          (item) =>
            item.name.toLowerCase() ===
            String(row.industry).trim().toLowerCase()
        )
      )
        issues.push({
          field: "Industry",
          message: "Select an active PIERS industry.",
        });
      for (const [field, value] of [["Due Date", row.dueDate], ["Financial Year End", row.source.financialYearEnd]]) {
        if (value && !isValidImportDate(value, { dateOrder: row.dateOrder, date1904: row.date1904 }))
          issues.push({ field, message: "Enter a valid date. For ambiguous slash dates, return to Upload and select the date order." });
      }
      for (const [key, label] of engagementFields.filter(([key]) =>
        key.startsWith("risk")
      )) {
        const value = String(row.source[key] || "").trim();
        if (value && !/^(y|n|yes|no|true|false|1|0)$/i.test(value))
          issues.push({ field: label, message: "Use Yes/No for risk values." });
      }
      const key = `${row.name.trim().toLowerCase()}|${String(
        row.clientCode || row.client
      )
        .trim()
        .toLowerCase()}`;
      if (!row.ignored && keys.has(key))
        issues.push({
          field: "Engagement",
          message:
            "Duplicate engagement in this file. Exclude the duplicate before importing.",
        });
      if (!row.ignored) keys.add(key);
      return {
        ...resolved,
        validationIssues: issues,
        included: !row.ignored && issues.length === 0,
        warningIssues: warnings,
        validation: issues.length
          ? "Error"
          : warnings.length
          ? "Warning"
          : "Ready",
      };
    });
  };

  const analyzeOwnFile = async () => {
    if (!sourceWorkbook || !file) return;
    if (sourceWorkbook.piersStructure?.headerEnd > sourceWorkbook.piersStructure?.headerStart && !headingReviewConfirmed) {
      setFileError("Review every detected heading row and confirm that none contains engagement data before continuing.");
      return;
    }
    setParsing(true);
    setFileError("");
    setDataStepReached(false);
    setMappingConfirmed(false);
    try {
      const matrix = XLSX.utils.sheet_to_json(
        sourceWorkbook.Sheets[sourceSheet],
        { header: 1, defval: "", raw: true, range: 0 }
      );
      const validationError = validateHeaderRow(matrix, sourceHeaderRow);
      if (validationError) throw new Error(validationError);
      const headers = matrix[sourceHeaderRow - 1].map((value) => String(value ?? "").trim());
      const reuseMappings = mappingSource.current?.workbook === sourceWorkbook &&
        mappingSource.current?.sheet === sourceSheet && mappingSource.current?.headerRow === sourceHeaderRow;
      let mappings = reuseMappings ? { ...columnMappings } : suggestExactMappings(headers),
        warnings = reuseMappings ? mappingWarnings : [],
        usedAI = reuseMappings ? aiUsed : false;
      if (!reuseMappings) try {
        const response = await postData("/dataimport/analyze-mappings", {
          headers,
        });
        if (response?.status) {
          const used = new Set();
          mappings = Object.fromEntries(
            engagementFields.map(([key]) => {
              const source =
                response.data?.mappings?.[key] || mappings[key] || "";
              if (key === "financialYearEnd" && ["financialyear", "fiscalyear", "fy"].includes(String(source).replace(/[^a-z0-9]/gi, "").toLowerCase()))
                return [key, ""];
              if (!headers.includes(source) || used.has(source))
                return [key, ""];
              used.add(source);
              return [key, source];
            })
          );
          usedAI = response.data?.aiUsed === true;
        } else
          warnings.push(
            response?.message ||
              "AI mapping is unavailable. Complete the mappings manually."
          );
      } catch {
        warnings.push(
          "AI mapping is unavailable. Complete the mappings manually."
        );
      }
      setSelectedImportTemplate({
        id: "ai-mapping",
        name: "Confirmed mapping",
        formatType: "XLSX",
        mappings: engagementFields.map(([key]) => ({
          source: mappings[key] || "",
          destination: key,
          ignore: !mappings[key],
        })),
      });
      if (!reuseMappings) initialMappings.current = { ...mappings };
      mappingSource.current = { workbook: sourceWorkbook, sheet: sourceSheet, headerRow: sourceHeaderRow };
      setMappingHeaders(headers);
      setColumnMappings(mappings);
      setMappingWarnings(warnings);
      setAiUsed(usedAI);
      setRows(rebuildMappedRows(mappings, rows, true));
      setSelectedRowIds([]);
      setStep(2);
    } catch (error) {
      setFileError(error.message || "Unable to analyze this file.");
    } finally {
      setParsing(false);
    }
  };

  const changeColumnMapping = (key, source) => {
    if (
      rows.some((row) => Object.keys(row.overrides || {}).length) &&
      !window.confirm(
        "Changing mappings will replace your engagement corrections. Continue?"
      )
    )
      return;
    setDataStepReached(false);
    const mappings = { ...columnMappings, [key]: source };
    setSelectedImportTemplate((current) => ({
      ...current,
      mappings: engagementFields.map(([field]) => ({
        source: mappings[field] || "",
        destination: field,
        ignore: !mappings[field],
      })),
    }));
    setColumnMappings(mappings);
    setMappingConfirmed(false);
    setRows(rebuildMappedRows(mappings, rows));
    setSelectedRowIds([]);
  };

  const parseFile = async () => {
    if (!file || !selectedImportTemplate || fileError) return;
    if (selectedImportTemplate.formatType !== "XLSX") {
      ErrorNotify("This import format is not available for validation yet.");
      return;
    }
    setParsing(true);
    const formData = new FormData();
    formData.append("importTypeId", importType === "client" ? "2" : "1");
    formData.append("inspectionCycleId", String(initialInspectionCycleId || 0));
    formData.append("importDefinitionId", String(selectedImportTemplate.id));
    formData.append(
      "importDefinitionVersion",
      String(selectedImportTemplate.version || "")
    );
    formData.append("importFile", file);
    try {
      const response = await postRawData(
        "/dataimport/readfile",
        "POST",
        formData
      );
      if (!response.status)
        throw new Error(response.message || "We could not read this workbook.");
      const parsed = normalizeRows(
        response.data?.rows,
        importType,
        selectedImportTemplate.structure
      );
      if (!parsed.length)
        throw new Error(
          `The ${
            selectedImportTemplate.structure?.worksheet || "required"
          } worksheet contains no data rows.`
        );
      setRows(
        importType === "client"
          ? parsed.map((row) => validateClientImportRow(row))
          : parsed.map((row) =>
              resolveEngagementClient(validateEngagementImportRow(row), clients)
            )
      );
      setSelectedRowIds([]);
      setStep(2);
      SuccessNotify("Workbook loaded and validation completed.");
    } catch (error) {
      ErrorNotify(error.message || "Unable to validate the selected workbook.");
    } finally {
      setParsing(false);
    }
  };

  const getSubmissionFile = async () => {
    if (compactEngagementFlow) {
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(canonicalEngagementRows(rows)),
        "Engagements"
      );
      return new File(
        [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
        "engagements.xlsx",
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );
    }
    const hasOverrides = rows.some(
      (row) => Object.keys(row.overrides || {}).length > 0
    );
    if (!file || (rows.every((row) => row.included) && !hasOverrides))
      return file;
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
    const sheetName =
      selectedImportTemplate?.structure?.worksheet || workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) return file;
    const includedSourceRows = new Set(
      rows.filter((row) => row.included).map((row) => row.sourceRow)
    );
    const worksheetRows = XLSX.utils.sheet_to_json(worksheet, {
      defval: null,
      header: 1,
      raw: true,
    });
    const configuredWorksheetRows = worksheetRows.map((row) => [...row]);
    const headers = configuredWorksheetRows[0] || [];
    if (
      isClientImport &&
      rows.some((row) => row.overrides?.isActive) &&
      !headers.some(
        (value) =>
          String(value || "")
            .replace(/[^a-z0-9]/gi, "")
            .toLowerCase() === "status"
      )
    ) {
      headers.push("Status");
    }
    const headerIndexByName = headers.reduce(
      (indexes, value, index) => ({
        ...indexes,
        [String(value || "")
          .replace(/[^a-z0-9]/gi, "")
          .toLowerCase()]: index,
      }),
      {}
    );

    const mappedHeaders = (destinationPattern) =>
      (selectedImportTemplate?.mappings || [])
        .filter((mapping) =>
          destinationPattern.test(
            String(mapping.destination || "")
              .replace(/[^a-z0-9]/gi, "")
              .toLowerCase()
          )
        )
        .map((mapping) =>
          String(mapping.source || "")
            .replace(/[^a-z0-9]/gi, "")
            .toLowerCase()
        )
        .filter(Boolean);
    const overrideHeaders = {
      assignedTo: [...mappedHeaders(/assignedto/), "assignedto"],
      description: [...mappedHeaders(/description/), "description"],
      dueDate: [
        ...mappedHeaders(/periodenddate|duedate/),
        "periodenddate",
        "duedate",
      ],
      engagementCode: [
        ...mappedHeaders(/engagementcode/),
        "engagementcode",
        "engno",
      ],
      externalEngagementId: [
        ...mappedHeaders(/externalengagementid/),
        "externalengagementid",
      ],
      industry: [...mappedHeaders(/industry/), "industry"],
      isActive: ["status", "isactive"],
      leadReviewer: [...mappedHeaders(/leadreviewer/), "leadreviewer"],
      manager: [...mappedHeaders(/manager/), "manager"],
      name: [...mappedHeaders(/engagementname/), "engagementname", "engdesc"],
      partner: [...mappedHeaders(/partner/), "engagementpartner", "partner"],
      risk: ["risklevel"],
      workType: isClientImport
        ? ["clienttype", "type"]
        : [
            ...mappedHeaders(/engagementtype|worktype/),
            "engagementtype",
            "worktype",
          ],
    };
    (selectedImportTemplate?.mappings || []).forEach((mapping) => {
      const destinationKey = String(mapping.destination || "")
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();
      const sourceKey = String(mapping.source || "")
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();
      if (destinationKey && sourceKey && !overrideHeaders[destinationKey])
        overrideHeaders[destinationKey] = [sourceKey, destinationKey];
    });
    configuredWorksheetRows.forEach((worksheetRow, index) => {
      const importRow = rows.find((row) => row.sourceRow === index + 1);
      if (
        !importRow ||
        index === 0 ||
        !Object.keys(importRow.overrides || {}).length
      )
        return;
      const configuredRow = worksheetRow;
      Object.keys(importRow.overrides).forEach((field) => {
        const headerIndex = overrideHeaders[field]
          ?.map((header) => headerIndexByName[header])
          .find((header) => header !== undefined);
        if (headerIndex !== undefined)
          configuredRow[headerIndex] =
            field === "isActive"
              ? importRow.isActive
                ? "Active"
                : "Inactive"
              : importRow[field];
      });
    });
    workbook.Sheets[sheetName] = XLSX.utils.aoa_to_sheet(
      configuredWorksheetRows.filter(
        (_, index) => index === 0 || includedSourceRows.has(index + 1)
      )
    );
    const content = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    return new File([content], file.name, {
      type:
        file.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const createRecords = async () => {
    if (!file) return;
    if (compactEngagementFlow) {
      if (!mappingConfirmed) return;
      const checked = validateOwnRows(rows);
      if (checked.some((row) => !row.ignored && row.validation === "Error")) {
        setRows(checked);
        ErrorNotify(
          "Resolve or exclude the records with errors before importing."
        );
        return;
      }
      if (!checked.some((row) => row.included)) return;
    }
    setCreating(true);
    if (compactEngagementFlow) {
      setStep(4);
      setImportError("");
    }
    const formData = new FormData();
    formData.append("importTypeId", importType === "client" ? "2" : "1");
    formData.append("inspectionCycleId", String(initialInspectionCycleId || 0));
    if (/^\d+$/.test(String(selectedImportTemplate?.id))) {
      formData.append("importDefinitionId", String(selectedImportTemplate.id));
      formData.append(
        "importDefinitionVersion",
        String(selectedImportTemplate.version || "")
      );
    }
    if (!isClientImport) {
      const includedEngagements = rows.filter((row) => row.included);
      formData.append(
        "resolutionData",
        JSON.stringify(
          includedEngagements.map((row, index) => ({
            sourceRow: index + 2,
            clientId: row.clientId,
            reviewTemplateId:
              reviewTemplates.find(
                (template) => template.name === row.assignedTemplate
              )?.id || 0,
          }))
        )
      );
    }
    try {
      formData.append("importFile", await getSubmissionFile());
      const response = await postRawData(
        "/dataimport/create",
        "POST",
        formData
      );
      if (!response.status)
        throw new Error(response.message || "Unable to start record creation.");
      const completedReport = response.data?.report || null;
      const importRecord = response.data?.import || response.data;
      const importStarted = {
        id:
          importRecord?.id ||
          importRecord?.importId ||
          importRecord?.dataImportId ||
          importRecord?.importJobId,
        importType,
        completed: Boolean(completedReport),
        ready:
          completedReport?.successCount ??
          rows.filter((row) => row.included).length,
        warnings: completedReport?.warningCount ?? counts.warning,
        failed: completedReport?.failureCount ?? 0,
        excluded: counts.excluded,
        firstEngagementId: completedReport?.firstEngagementId,
        engagements:
          completedReport?.engagements ||
          rows
            .filter((row) => row.included)
            .map((row) => ({
              id: row.id,
              name: row.name,
              clientId: row.clientId,
              clientName: row.client,
              workType: row.workType,
              industryType: row.industry,
            })),
      };

      if (completedReport) {
        setImportReport(importStarted);
        SuccessNotify(
          `${importStarted.ready} engagement${
            importStarted.ready === 1 ? "" : "s"
          } imported successfully.`
        );
      }

      if (onImportStarted) {
        await onImportStarted(importStarted);
        return;
      }

      if (completedReport) return;
      if (compactEngagementFlow)
        throw new Error(
          "Import started but results are unavailable. Check Import History before starting another import."
        );

      closeAction?.();
      navigate("/piers/dataimport", { state: { importStarted } });
    } catch (error) {
      setImportError(
        error.message ||
          "Unable to create records. Check Import History before retrying."
      );
      ErrorNotify(error.message || "Unable to create records.");
    } finally {
      setCreating(false);
    }
  };

  const fileSummary = (
    <section className="engagement-import__file-summary">
      <i className="bx bxs-file-xls" />
      <div>
        <b>{file?.name}</b>
        <span>
          {importTypeDetails[importType]?.label} Import ·{" "}
          {compactEngagementFlow
            ? "Your existing format"
            : selectedImportTemplate?.name}{" "}
          ·{" "}
          {compactEngagementFlow
            ? "AI-assisted mapping"
            : formatLabel(selectedImportTemplate?.formatType)}
        </span>
      </div>
      <strong>
        {counts.total}
        <small>Total Records</small>
      </strong>
      <strong className="is-green">
        {counts.ready}
        <small>Ready</small>
      </strong>
      <strong className="is-orange">
        {counts.warning}
        <small>Warnings</small>
      </strong>
      <strong className="is-red">
        {counts.error}
        <small>Errors</small>
      </strong>
      {(compactEngagementFlow ? step === 3 : step === 2) && (
        <aside className="engagement-import__file-summary-actions">
          <Button
            className="engagement-import__revalidate-button"
            color="primary"
            onClick={revalidateCurrentRows}
          >
            <i className="bx bx-refresh" /> Revalidate
          </Button>
        </aside>
      )}
    </section>
  );

  const liveGuidance = compactEngagementFlow
    ? parsing
      ? "Analyzing column mappings. No engagements have been created."
      : step === 1
      ? "Upload your existing Excel, CSV or PDF file. No PIERS template is required."
      : step === 2
      ? "Confirm the column mappings, then continue to update the data."
      : step === 3
      ? "Edit engagement data, resolve issues, and confirm before importing."
      : "Import results will appear here when processing finishes."
    : choosingImportType
    ? "Choose the type of records you want to import."
    : parsing
    ? "Validating the workbook. Your records have not been created yet."
    : creating
    ? `Starting the import for ${
        rows.filter((row) => row.included).length
      } included records. Please keep this page open.`
    : fileError
    ? fileError
    : step === 1
    ? file
      ? "File selected. Validate & Continue will read the records and check their values."
      : "Select a template, then upload your completed workbook."
    : counts.error > 0
    ? `${counts.total} records found. ${counts.error} records have blocking errors. Use the Errors filter and Fix/Edit Data, then revalidate.`
    : step === 2
    ? `${counts.total} records found. No blocking errors remain. Review ${
        counts.warning
      } warnings before ${
        compactEngagementFlow ? "creating" : "confirming"
      } the included records.`
    : "Confirm the included records. Creation runs in the background; check the Import Status Report before starting preparation.";

  const pageFooter = compactEngagementFlow ? null : choosingImportType ? (
    <div className="modal-footer canvasFooter engagement-import__modal-footer">
      <Button outline onClick={exitImport}>
        Cancel
      </Button>
      <Button
        color="primary"
        disabled={!importType}
        onClick={() => setChoosingImportType(false)}
      >
        Next <i className="bx bx-right-arrow-alt" />
      </Button>
    </div>
  ) : (
    <div
      className={`modal-footer canvasFooter engagement-import__modal-footer ${
        step === 2
          ? "engagement-import__validation-footer"
          : step === 3 && !compactEngagementFlow
          ? "engagement-import__review-footer"
          : ""
      }`}
    >
      {step === 1 && (
        <>
          {!compactEngagementFlow && (
            <>
              <Button
                className="engagement-import__back-button"
                outline
                onClick={() =>
                  isPage && initialImportType
                    ? exitImport()
                    : setChoosingImportType(true)
                }
              >
                <i className="bx bx-left-arrow-alt" /> Back
              </Button>
              <Button outline onClick={exitImport}>
                Cancel
              </Button>
            </>
          )}
          <Button
            color="primary"
            disabled={!file || parsing}
            onClick={parseFile}
          >
            {parsing ? "Analyzing file..." : "Analyze & Continue"}{" "}
            <i className="bx bx-right-arrow-alt" />
          </Button>
        </>
      )}
      {step === 2 && (
        <>
          {!compactEngagementFlow && (
            <Button
              className="engagement-import__back-button"
              outline
              onClick={() => setStep(1)}
            >
              <i className="bx bx-left-arrow-alt" /> Back
            </Button>
          )}
          <span className="engagement-import__validation-message">
            <i className="bx bx-error-circle" /> {counts.error} record
            {counts.error === 1 ? " has" : "s have"} blocking errors
          </span>
          <div className="engagement-import__validation-actions">
            <Button
              color="primary"
              disabled={
                counts.error > 0 ||
                creating ||
                !rows.some((row) => row.included)
              }
              onClick={() =>
                compactEngagementFlow ? createRecords() : setStep(3)
              }
            >
              {compactEngagementFlow
                ? creating
                  ? "Starting Import..."
                  : `Create ${
                      rows.filter((row) => row.included).length
                    } Engagements`
                : "Confirm & Create"}{" "}
              <i
                className={`bx ${
                  creating ? "bx-loader-alt bx-spin" : "bx-right-arrow-alt"
                }`}
              />
            </Button>
          </div>
        </>
      )}
      {!compactEngagementFlow && step === 3 && (
        <>
          <Button outline disabled={creating} onClick={() => setStep(2)}>
            <i className="bx bx-left-arrow-alt" /> Back to Validate & Configure
          </Button>
          <div>
            <Button outline disabled={creating} onClick={exitImport}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={creating || !rows.some((row) => row.included)}
              onClick={createRecords}
            >
              {creating
                ? "Starting Import..."
                : `Create ${rows.filter((row) => row.included).length} ${
                    isClientImport ? "Clients" : "Engagements"
                  }`}{" "}
              <i
                aria-hidden="true"
                className={`bx ${
                  creating ? "bx-loader-alt bx-spin" : "bx-right-arrow-alt"
                }`}
              />
            </Button>
          </div>
        </>
      )}
    </div>
  );

  if (compactEngagementFlow && importReport) {
    return (
      <main className={(pageClassName) + " piers-page"}>
        <Stepper
          currentStep={4}
          importType={importType}
          compact
          maxReachableStep={4}
          busy
          onSelectStep={() => {}}
        />
        <section
          className="engagement-import__final-report"
          aria-labelledby="engagement-import-report-title"
        >
          {embeddedHeader && (
            <div className="engagement-import__report-onboarding-header">
              {embeddedHeader}
            </div>
          )}
          <header>
            <span>
              <i className="bx bx-check" aria-hidden="true" />
            </span>
            <div className="piers-page-header">
              <p>
                {importReport.failed
                  ? "IMPORT COMPLETED WITH ISSUES"
                  : "IMPORT COMPLETE"}
              </p>
              <h1 className="piers-page-title" id="engagement-import-report-title">
                Engagement import report
              </h1>
              <small>
                Review the engagements created by this import and any processing
                issues.
              </small>
            </div>
          </header>
          <div className="engagement-import__final-report-totals">
            <article>
              <strong>{importReport.ready}</strong>
              <span>Created</span>
            </article>
            <article>
              <strong>{importReport.warnings}</strong>
              <span>Warnings</span>
            </article>
            <article>
              <strong>{importReport.failed}</strong>
              <span>Failed</span>
            </article>
            <article>
              <strong>{importReport.excluded}</strong>
              <span>Excluded</span>
            </article>
          </div>
          {importReport.failed > 0 && (
            <p role="alert">
              Some records failed.{" "}
              <button
                type="button"
                onClick={() => navigate("/piers/dataimport")}
              >
                Review failures in Import History
              </button>
            </p>
          )}
          <section className="engagement-import__final-report-list">
            <h2>Imported engagements</h2>
            <Input
              aria-label="Search imported engagements"
              placeholder="Search imported engagements"
              value={resultSearch}
              onChange={(event) => setResultSearch(event.target.value)}
            />
            <div
              className="engagement-import__final-report-table"
              role="table"
              aria-label="Imported engagements"
            >
              <div role="row" className="is-heading">
                <span role="columnheader">Engagement ID</span>
                <span role="columnheader">Engagement</span>
                <span role="columnheader">Client / Firm</span>
                <span role="columnheader">Work Type</span>
                <span role="columnheader">Industry Type</span>
                <span role="columnheader">Result</span>
              </div>
              {(importReport.engagements || [])
                .filter((item) =>
                  [item.name, item.clientName, item.engagementNo].some((value) =>
                    String(value || "")
                      .toLowerCase()
                      .includes(resultSearch.toLowerCase())
                  )
                )
                .map((engagement) => (
                  <div role="row" key={engagement.id || engagement.name}>
                    <span role="cell">{engagement.engagementNo || "—"}</span>
                    <strong role="cell">{engagement.name}</strong>
                    <span role="cell">{engagement.clientName || "—"}</span>
                    <span role="cell">{engagement.workType || "—"}</span>
                    <span role="cell">{engagement.industryType || "—"}</span>
                    <span role="cell" className="is-created">
                      <i className="bx bx-check" /> Created
                    </span>
                  </div>
                ))}
            </div>
          </section>
          <footer>
            <Button outline onClick={resetImport}>
              <i className="bx bx-upload" /> Import another workbook
            </Button>
            <Button color="primary" onClick={leaveImport}>
              View all engagements
            </Button>
          </footer>
        </section>
      </main>
    );
  }

  return (
    <main className={(pageClassName) + " piers-page"}>
      {isPage && !hidePageHeader ? (
        <header className="engagement-import__header piers-page-header">
          <button
            type="button"
            onClick={exitImport}
            disabled={creating || parsing}
          >
            ← Back to {isClientImport ? "Clients" : "Engagements"}
          </button>
          <h1 className="piers-page-title">Import {isClientImport ? "Clients" : "Engagements"}</h1>
          <p>
            {cycle
              ? `Inspection cycle: ${
                  cycle.projectTitle ||
                  cycle.title ||
                  cycle.name ||
                  cycle.projectNo
                } · ${cycle.projectNo || ""}`
              : "Import information and metadata into PIERS."}
          </p>
        </header>
      ) : !isPage ? (
        <ImportHeader
          closeAction={exitImport}
          currentStep={currentStep}
          importType={importType}
          onSelectStep={selectStepperStep}
          compact={compactEngagementFlow}
          maxReachableStep={maxReachableStep}
        />
      ) : null}
      <div className="engagement-import__scrollable-content">
        {embedded && embeddedHeader && (
          <div className="engagement-import__embedded-header">
            {embeddedHeader}
            <Stepper
              currentStep={currentStep}
              importType={importType}
              onSelectStep={selectStepperStep}
              lockedType={Boolean(initialImportType)}
              busy={creating || parsing}
              compact={compactEngagementFlow}
              maxReachableStep={maxReachableStep}
            />
          </div>
        )}
        <div className="engagement-import__step-layout">
          {isPage && !(embedded && embeddedHeader) && (
            <Stepper
              currentStep={currentStep}
              importType={importType}
              onSelectStep={selectStepperStep}
              lockedType={Boolean(initialImportType)}
              busy={creating || parsing}
              compact={compactEngagementFlow}
              maxReachableStep={maxReachableStep}
            />
          )}
          <div className="engagement-import__work-area">
            {!embedded && (
              <StepHelpCard
                liveMessage={liveGuidance}
                importType={importType}
                currentStep={currentStep}
                ownFormat={compactEngagementFlow}
                guidanceStep={compactEngagementFlow ? step + 1 : currentStep}
                totalSteps={4}
                key={currentStep}
                onStartTour={startCurrentStepTour}
              />
            )}
            <div className="engagement-import__step-content">
              {choosingImportType && (
                <ImportTypeStep
                  importType={importType}
                  onSelect={selectImportType}
                />
              )}
              {!choosingImportType && step === 1 && !compactEngagementFlow && (
                <UploadStep
                  definitions={definitions}
                  file={file}
                  fileError={fileError}
                  importType={importType}
                  onFileSelected={validateSelectedFile}
                  onRemoveFile={() => {
                    setFile(null);
                    setFileError("");
                  }}
                  onSelectTemplate={selectImportTemplate}
                  selectedTemplate={selectedImportTemplate}
                />
              )}
              {compactEngagementFlow && step === 1 && (
                <EngagementUploadStep
                  file={file}
                  error={fileError}
                  headerRowError={headerRowError}
                  structureNotice={structureNotice}
                  detectedStructure={sourceWorkbook?.piersStructure}
                  headingReviewConfirmed={headingReviewConfirmed}
                  onConfirmHeadings={setHeadingReviewConfirmed}
                  dateOrder={dateOrder}
                  onDateOrder={(value) => {
                    setDateOrder(value);
                    setMappingConfirmed(false);
                    setDataStepReached(false);
                    setFileError("");
                  }}
                  onUseOriginal={originalWorkbook && sourceWorkbook !== originalWorkbook ? () => {
                    setSourceWorkbook(originalWorkbook);
                    setSourceSheet(originalWorkbook.SheetNames[0]);
                    setSourceHeaderRow(suggestHeaderRow(XLSX.utils.sheet_to_json(originalWorkbook.Sheets[originalWorkbook.SheetNames[0]], { header: 1, defval: "", raw: true, range: 0 })));
                    setStructureNotice("Original worksheets restored. Choose the intended worksheet and headings row. Complex layouts must be flattened for manual mapping.");
                    setRows([]);
                    deletedSourceRows.current.clear();
                    setSelectedRowIds([]);
                    setMappingConfirmed(false);
                    setDataStepReached(false);
                  } : undefined}
                  onFile={readOwnFile}
                  onRemove={() => {
                    setFile(null);
                    setSourceWorkbook(null);
                    setRows([]);
                    deletedSourceRows.current.clear();
                  }}
                  sheets={sourceWorkbook?.SheetNames || []}
                  sheet={sourceSheet}
                  onSheet={(name) => {
                    setFileError("");
                    setRows([]);
                    deletedSourceRows.current.clear();
                    setSelectedRowIds([]);
                    setMappingConfirmed(false);
                    setDataStepReached(false);
                    setSourceSheet(name);
                    setSourceHeaderRow(suggestHeaderRow(XLSX.utils.sheet_to_json(
                      sourceWorkbook.Sheets[name],
                      { header: 1, defval: "", raw: true, range: 0 }
                    )));
                  }}
                  previewRows={sourceWorkbook?.Sheets[sourceSheet] ? XLSX.utils.sheet_to_json(
                    sourceWorkbook.Sheets[sourceSheet],
                    { header: 1, defval: "", raw: false, range: 0 }
                  ).slice(sourceHeaderRow - 1, sourceHeaderRow + 2) : []}
                  headerRow={sourceHeaderRow}
                  onHeaderRow={(value) => {
                    setRows([]);
                    deletedSourceRows.current.clear();
                    setSelectedRowIds([]);
                    setMappingConfirmed(false);
                    setDataStepReached(false);
                    setSourceHeaderRow(value);
                    setFileError("");
                  }}
                  busy={parsing}
                />
              )}
              {compactEngagementFlow && step === 1 && (
                <div>
                  <p id="engagement-analyze-status" role="status" className={headerRowError ? "text-danger" : "text-muted"}>
                    {parsing ? "Please wait while your file is processed." : !file
                      ? "Choose a file to enable Analyze & Continue."
                      : sourceWorkbook?.piersStructure?.headerEnd > sourceWorkbook?.piersStructure?.headerStart && !headingReviewConfirmed ? "Analyze & Continue is unavailable: review and confirm the detected heading rows below. If any row contains engagement data, restore the original worksheets instead."
                      : headerRowError ? `Analyze & Continue is unavailable: ${headerRowError}`
                      : `Ready to analyze using headings on row ${sourceHeaderRow}. Engagements are read from the rows below.`}
                  </p>
                  <div className="engagement-import__inline-actions">
                  <Button
                    color="primary"
                    disabled={
                      (!compactEngagementFlow && !selectedImportTemplate) ||
                      !file ||
                      !!headerRowError ||
                      (sourceWorkbook?.piersStructure?.headerEnd > sourceWorkbook?.piersStructure?.headerStart && !headingReviewConfirmed) ||
                      parsing
                    }
                    aria-describedby="engagement-analyze-status"
                    onClick={analyzeOwnFile}
                  >
                    {parsing ? "Analyzing file..." : "Analyze & Continue"}{" "}
                    <i className="bx bx-right-arrow-alt" />
                  </Button>
                  </div>
                </div>
              )}
              {compactEngagementFlow && step === 2 && (
                <>
                  <EngagementMappingReview
                    headers={mappingHeaders}
                    mappings={columnMappings}
                    samples={mappingSamples}
                    warnings={mappingWarnings}
                    aiUsed={aiUsed}
                    onChange={changeColumnMapping}
                    confirmed={mappingConfirmed}
                    onConfirm={setMappingConfirmed}
                    onReset={() => {
                      if (
                        rows.some(
                          (row) => Object.keys(row.overrides || {}).length
                        ) &&
                        !window.confirm(
                          "Resetting mappings will replace your engagement corrections. Continue?"
                        )
                      )
                        return;
                      const mappings = { ...initialMappings.current };
                      setColumnMappings(mappings);
                      setMappingConfirmed(false);
                      setDataStepReached(false);
                      setSelectedImportTemplate((current) => ({
                        ...current,
                        mappings: engagementFields.map(([field]) => ({
                          source: mappings[field] || "",
                          destination: field,
                          ignore: !mappings[field],
                        })),
                      }));
                      setRows(rebuildMappedRows(mappings, rows));
                      setSelectedRowIds([]);
                    }}
                    onBack={() => setStep(1)}
                    canContinue={Boolean(
                      mappingConfirmed &&
                        columnMappings.engagementName &&
                        columnMappings.workType &&
                        (columnMappings.clientName || columnMappings.clientCode)
                    )}
                    onContinue={() => {
                      setDataStepReached(true);
                      setRows(validateOwnRows(rows));
                      setStep(3);
                    }}
                  />
                </>
              )}
              {compactEngagementFlow && step === 3 && (
                <>
                  <h2>Update engagement data</h2>
                  <p>
                    Review each engagement, update its data, and resolve or
                    exclude rows with errors before importing.
                  </p>
                </>
              )}
              {!choosingImportType &&
                (compactEngagementFlow ? step === 3 : step === 2) && (
                  <section className="engagement-import__workspace">
                    {importReport && (
                      <section
                        className="engagement-import__completion-report"
                        role="status"
                      >
                        <i className="bx bx-check-circle" aria-hidden="true" />
                        <div>
                          <strong>Engagement import complete</strong>
                          <span>
                            {importReport.ready} created •{" "}
                            {importReport.warnings} warnings •{" "}
                            {importReport.failed} failed
                          </span>
                        </div>
                      </section>
                    )}
                    {fileSummary}
                    <div className="engagement-import__toolbar">
                      <div className="engagement-import__row-actions">
                        {isClientImport && (
                          <div className="engagement-import__bulk-actions">
                            <Button
                              disabled={!selectedClientRows.length}
                              outline
                              onClick={() =>
                                setBulkMenuOpen((current) => !current)
                              }
                            >
                              Bulk Actions <i className="bx bx-chevron-down" />
                            </Button>
                            {bulkMenuOpen && (
                              <div
                                className="engagement-import__bulk-menu"
                                role="menu"
                              >
                                {clientBulkEditableFields.map((field) => (
                                  <button
                                    key={field.field}
                                    onClick={() => {
                                      setBulkField(field);
                                      setBulkValue(
                                        field.field === "isActive"
                                          ? "Active"
                                          : ""
                                      );
                                      setBulkOverwrite(false);
                                      setBulkMenuOpen(false);
                                    }}
                                    type="button"
                                  >
                                    Set {field.label}
                                  </button>
                                ))}
                                <hr />
                                <button
                                  onClick={toggleSelectedImportInclusion}
                                  type="button"
                                >
                                  {selectedClientRows.every(
                                    (row) => row.ignored
                                  )
                                    ? "Include in Import"
                                    : "Exclude from Import"}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <Button outline onClick={toggleSelectedImportInclusion}>
                          <i
                            className={`bx ${
                              selectedRows.length &&
                              selectedRows.every((row) => row.ignored)
                                ? "bx-show"
                                : "bx-hide"
                            }`}
                          />{" "}
                          {selectedRows.length &&
                          selectedRows.every((row) => row.ignored)
                            ? "Include in Import"
                            : "Exclude Selected"}
                          {selectedRowIds.length > 0 &&
                            ` (${selectedRowIds.length})`}
                        </Button>
                        <Button
                          className="engagement-import__delete-selected"
                          outline
                          onClick={deleteSelectedRows}
                        >
                          <i className="bx bx-trash" /> Delete Selected
                          {selectedRowIds.length > 0 &&
                            ` (${selectedRowIds.length})`}
                        </Button>
                      </div>
                      <div className="engagement-import__toolbar-controls">
                        <div className="engagement-import__toolbar-filters">
                          {[
                            ["all", "All", counts.total, "bx-list-ul"],
                            ["ready", "Ready", counts.ready, "bx-check-circle"],
                            ["warning", "Warnings", counts.warning, "bx-error"],
                            [
                              "error",
                              "Errors",
                              counts.error,
                              "bx-error-circle",
                            ],
                            [
                              "excluded",
                              "Excluded",
                              counts.excluded,
                              "bx-hide",
                            ],
                          ].map(([value, label, count, icon]) => (
                            <button
                              className={
                                validationFilter === value ? "is-active" : ""
                              }
                              key={label}
                              onClick={() => setValidationFilter(value)}
                              type="button"
                            >
                              <i className={`bx ${icon}`} />
                              <span>{label}</span>
                              <b>{count}</b>
                            </button>
                          ))}
                        </div>
                        <Input
                          onChange={(event) =>
                            setValidationSearch(event.target.value)
                          }
                          placeholder={`Search ${
                            isClientImport
                              ? "clients"
                              : "engagements, clients, users"
                          }...`}
                          value={validationSearch}
                        />
                      </div>
                    </div>
                    <div className="engagement-import__split">
                      <ImportTable
                        importType={importType}
                        onOpen={(row) => setDrawer(row)}
                        onToggleAll={toggleAllRowSelection}
                        onToggleRow={toggleRowSelection}
                        rows={validatedRows}
                        selectable
                        selectedRowIds={selectedRowIds}
                      />
                      {isClientImport ? (
                        <ClientConfigureDrawer
                          clientTypes={clientTypes}
                          industryTypes={industryTypes}
                          onApply={(id, changes) => {
                            applyClientChanges(id, changes);
                            setDrawer(null);
                          }}
                          onClose={() => setDrawer(null)}
                          row={drawer}
                        />
                      ) : (
                        <ImportedEngagementCorrectionDrawer
                          clients={clients}
                          industryTypes={industryTypes}
                          mappings={selectedImportTemplate?.mappings}
                          onClose={() => setDrawer(null)}
                          onOpenNewClientDrawer={openNewClientDrawer}
                          onApply={(id, changes, changedCount) => {
                            applyEngagementChanges(id, changes);
                            setDrawer(null);
                            const remainingIssues =
                              changes.validationIssues?.length || 0;
                            SuccessNotify(
                              `${changedCount ? changedCount : "No"} correction${
                                changedCount === 1 ? "" : "s"
                              } saved for this import.${
                                remainingIssues
                                  ? ` ${remainingIssues} blocking issue${
                                      remainingIssues === 1 ? "" : "s"
                                    } remain.`
                                  : " The record is ready to create."
                              }`
                            );
                          }}
                          row={drawer}
                          reviewTemplates={reviewTemplates}
                          users={users}
                          validateRow={
                            compactEngagementFlow
                              ? (draft) => validateOwnRows(rows.map((row) => row.id === draft.id ? draft : row)).find((row) => row.id === draft.id)
                              : validateEngagementImportRow
                          }
                          workTypes={workTypes}
                        />
                      )}
                      <NewClientDrawer
                        clientTypes={clientTypes}
                        industryTypes={industryTypes}
                        isOpen={newClientDrawerOpen}
                        onClose={closeNewClientDrawer}
                        onSaved={selectNewClient}
                      />
                    </div>
                  </section>
                )}
              {compactEngagementFlow && step === 3 && (
                <footer className="engagement-import__data-footer">
                  <Button
                    type="button"
                    outline
                    disabled={creating}
                    onClick={() => setStep(2)}
                  >
                    <i className="bx bx-left-arrow-alt" aria-hidden="true" />{" "}
                    Back
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    disabled={
                      counts.error > 0 ||
                      creating ||
                      !mappingConfirmed ||
                      !rows.some((row) => row.included)
                    }
                    onClick={createRecords}
                  >
                    {creating
                      ? "Importing..."
                      : `Import ${
                          rows.filter((row) => row.included).length
                        } Engagement${
                          rows.filter((row) => row.included).length === 1
                            ? ""
                            : "s"
                        }`}{" "}
                    <i className="bx bx-right-arrow-alt" aria-hidden="true" />
                  </Button>
                </footer>
              )}
              {compactEngagementFlow && step === 4 && (
                <section
                  className="engagement-import__workspace"
                  aria-live="polite"
                  aria-busy={creating}
                >
                  <h2>
                    {creating
                      ? "Importing engagements"
                      : "Import needs attention"}
                  </h2>
                  {creating ? (
                    <>
                      <p>
                        Processing {rows.filter((row) => row.included).length}{" "}
                        engagements. Results will appear here when the import
                        finishes.
                      </p>
                      <div
                        role="progressbar"
                        aria-label="Importing engagements"
                        className="engagement-import__creation-progress-bar"
                      >
                        <span />
                      </div>
                    </>
                  ) : (
                    <>
                      <p role="alert">{importError}</p>
                      <Button
                        outline
                        onClick={() => navigate("/piers/dataimport")}
                      >
                        View Import History
                      </Button>
                    </>
                  )}
                </section>
              )}
              {!choosingImportType && step === 3 && !compactEngagementFlow && (
                <section
                  aria-busy={creating}
                  className="engagement-import__workspace"
                >
                  {fileSummary}
                  {creating && (
                    <section
                      aria-live="polite"
                      className="engagement-import__creation-progress"
                      role="status"
                    >
                      <i
                        aria-hidden="true"
                        className="bx bx-loader-alt bx-spin"
                      />
                      <div>
                        <strong>Starting import</strong>
                        <span>
                          Uploading the workbook and preparing{" "}
                          {rows.filter((row) => row.included).length}{" "}
                          {isClientImport ? "client" : "engagement"}
                          {rows.filter((row) => row.included).length === 1
                            ? ""
                            : "s"}
                          . Please keep this page open.
                        </span>
                        <div
                          aria-hidden="true"
                          className="engagement-import__creation-progress-bar"
                        >
                          <span />
                        </div>
                      </div>
                    </section>
                  )}
                  <section className="engagement-import__review-summary">
                    <article>
                      <i className="bx bx-file" />
                      <strong>
                        {rows.filter((row) => row.included).length}
                      </strong>
                      <span>
                        {isClientImport ? "Clients Ready" : "Engagements Ready"}
                      </span>
                    </article>
                    <article>
                      <i
                        className={`bx ${
                          isClientImport ? "bx-edit-alt" : "bx-buildings"
                        }`}
                      />
                      <strong>
                        {isClientImport
                          ? rows.filter(
                              (row) =>
                                Object.keys(row.overrides || {}).length > 0
                            ).length
                          : rows.filter((row) => row.clientId > 0).length}
                      </strong>
                      <span>
                        {isClientImport
                          ? "Clients Updated During Configuration"
                          : "Existing Clients Matched"}
                      </span>
                    </article>
                    <article>
                      <i
                        className={`bx ${
                          isClientImport ? "bx-hide" : "bx-group"
                        }`}
                      />
                      <strong>
                        {isClientImport ? counts.excluded : teamMembersSupplied}
                      </strong>
                      <span>
                        {isClientImport
                          ? "Excluded from Import"
                          : "Team Members Supplied"}
                      </span>
                    </article>
                    <article>
                      <i
                        className={`bx ${
                          isClientImport ? "bx-error-circle" : "bx-check-shield"
                        }`}
                      />
                      <strong>
                        {isClientImport ? counts.error : counts.configured}
                      </strong>
                      <span>
                        {isClientImport
                          ? "Require Attention"
                          : "Review Templates Assigned"}
                      </span>
                    </article>
                    <aside>
                      <b>File Information</b>
                      <span>{file?.name}</span>
                      <span>Uploaded just now</span>
                      <span>{counts.total} total rows</span>
                    </aside>
                  </section>
                  {counts.warning > 0 && (
                    <section className="engagement-import__warnings">
                      <b>
                        <i className="bx bx-error" /> Warnings ({counts.warning}
                        )
                      </b>
                      <span>
                        {isClientImport
                          ? "Some imported client values require attention before those clients can be created."
                          : "Some engagements are missing optional team assignments. They will be created without the missing assignments."}
                      </span>
                    </section>
                  )}
                  <div className="engagement-import__review-grid">
                    <div>
                      <header>
                        <h2>
                          {isClientImport ? "Clients" : "Engagements"} to be
                          Created ({rows.filter((row) => row.included).length})
                        </h2>
                        <Input placeholder="Search in this list..." />
                      </header>
                      <ImportTable
                        importType={importType}
                        onOpen={() => undefined}
                        rows={rows.filter((row) => row.included)}
                      />
                    </div>
                    <aside>
                      <h2>Create Options</h2>
                      <label>
                        <input defaultChecked type="checkbox" /> Include records
                        with warnings
                        <small>
                          {isClientImport ? "Clients" : "Engagements"} with
                          warnings will be created.
                        </small>
                      </label>
                      <label>
                        <input defaultChecked type="checkbox" /> Skip records
                        with blocking errors
                        <small>
                          Only valid{" "}
                          {isClientImport ? "clients" : "engagements"} will be
                          created.
                        </small>
                      </label>
                      <p>
                        <i className="bx bx-info-circle" /> {counts.error}{" "}
                        {isClientImport ? "client" : "engagement"}
                        {counts.error === 1 ? " has" : "s have"} blocking errors
                        and will be skipped.
                      </p>
                    </aside>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
      {pageFooter}

      <AppModal
        centered
        isOpen={Boolean(bulkField)}
        toggle={() => setBulkField(null)}
      >
        <ModalHeader toggle={() => setBulkField(null)}>
          Set {bulkField?.label}
        </ModalHeader>
        <ModalBody>
          <p className="engagement-import__bulk-modal-copy">
            Apply {bulkField?.label} to {selectedClientRows.length} selected
            client{selectedClientRows.length === 1 ? "" : "s"}.
          </p>
          <label className="engagement-import__bulk-modal-field">
            <span className="engagement-import__field-label">
              {bulkField?.label} {bulkField?.required && <em>*</em>}
            </span>
            <Input
              onChange={(event) => setBulkValue(event.target.value)}
              type="select"
              value={bulkValue}
            >
              <option value="">Select {bulkField?.label?.toLowerCase()}</option>
              {bulkOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Input>
          </label>
          {bulkField?.allowOverwrite && (
            <fieldset className="engagement-import__bulk-overwrite">
              <legend>Apply to</legend>
              <label>
                <input
                  checked={!bulkOverwrite}
                  onChange={() => setBulkOverwrite(false)}
                  type="radio"
                />
                Only selected clients with empty {bulkField.label}
              </label>
              <label>
                <input
                  checked={bulkOverwrite}
                  onChange={() => setBulkOverwrite(true)}
                  type="radio"
                />
                All selected clients
              </label>
            </fieldset>
          )}
        </ModalBody>
        <ModalFooter>
          <Button outline onClick={() => setBulkField(null)}>
            Cancel
          </Button>
          <Button color="primary" onClick={applyBulkClientValue}>
            Apply to {selectedClientRows.length} Client
            {selectedClientRows.length === 1 ? "" : "s"}
          </Button>
        </ModalFooter>
      </AppModal>
      <AppModal
        centered
        isOpen={Boolean(pendingImportTemplate)}
        toggle={() => setPendingImportTemplate(null)}
      >
        <ModalHeader toggle={() => setPendingImportTemplate(null)}>
          Change Import Template?
        </ModalHeader>
        <ModalBody>
          Changing the import template will remove the currently uploaded file
          because the file must be validated against the selected template.
        </ModalBody>
        <ModalFooter>
          <Button outline onClick={() => setPendingImportTemplate(null)}>
            Cancel
          </Button>
          <Button color="primary" onClick={confirmImportTemplateChange}>
            Change Template
          </Button>
        </ModalFooter>
      </AppModal>
    </main>
  );
}
